import * as React from 'react';
import BPromise from 'bluebird';
import Lazy from 'lazy.js';
import { AppUser } from 'Src/views/App/App';
import propz from 'propz';
import moment from 'moment';
import { parse, stringify } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE, DEFAULT_LIMIT, DEFAULT_SKIP, LIMIT } from 'Src/consts/table';
import { Loader } from 'Src/components/Loader/Loader';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import { Grid2 } from 'Src/components/Grid/Grid2';
import {
  getBirthday,
  getGender,
  getStudentTags,
  getStudentHouses,
  getStudentSpecialEducationNeeds,
  getStudentPupilsPremium,
  getStudentFreeSchoolMeals
} from 'Src/helpers/accessor/accessor';
import { SchoolStudent } from 'Src/models/schoolStudent';
import { Tag } from 'Src/models/tag';
import { getAllForms } from 'Src/helpers/service/admin/forms';
import { createUserTag, getAllUserTags } from 'Src/helpers/service/admin/userTags';
import { SchoolForm } from 'Src/models/form';
import {
  getAllSchoolStudents,
  searchSchoolStudents,
  updateSchoolStudent,
  updateStudentTagsInBulk,
  removeStudentsInBulk
} from 'Src/helpers/service/admin/students';
import {
  getSelectOptionForHouses,
  getSelectOptionForAge,
  getSelectOptionForForms,
  getSelectOptionForGender,
  getSelectOptionForTag,
  getSelectOptionForBooleanQuestion
} from 'Src/helpers/table/select';
import { STUDENT_TABS } from 'Src/consts/school';
import { StudentSummary } from 'Src/components/StudentSummary/StudentSummary';
import { UserTagDeleteModal } from 'Src/components/UserTagDeleteModal/UserTagDeleteModal';
import { UserTagAssignModal } from 'Src/components/UserTagAssignModal/UserTagAssignModal';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { GeneralMessageWizzard } from 'Src/components/GeneralMessageWizzard/GeneralMessageWizzard';
import { getSettingsUploadFiles } from 'Src/helpers/service/nobody/settings';
import { getStudentsParents } from 'Src/helpers/accessor/accessor';
import { StudentNextOfKin } from '../../../../../components/StudentNextOfKin/StudentNextOfKin';
import { StudentNextOfKinForm } from '../../../../../components/StudentNextOfKin/StudentNextOfKinForm';
import { addSchoolStudent } from '../../../../../helpers/service/admin/students';
import { StudentForm } from '../../../../../components/StudentForm/StudentForm';
import { getAllHouses } from 'Src/helpers/service/admin/houses';
import { getFullName } from 'Src/helpers/autocomplete/autocomplete';
import { uploadReport } from 'Src/helpers/service/admin/report';
import { REPORT_TYPE_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/report';
import { StudentParticipationInEventsReportForm } from './Reports/StudentParticipationInEventsReportForm/StudentParticipationInEventsReportForm';
import { StudentParticipationStatisticsByActivitiesReportForm } from './Reports/StudentParticipationStatisticsByActivitiesReportForm/StudentParticipationStatisticsByActivitiesReportForm';
import { ClubListForStudentReportForm } from './Reports/ClubListForStudentReportForm/ClubListForStudentReportForm';
import { SelectedStudentTargetCompletionReportForm } from './Reports/SelectedStudentTargetCompletionReportForm/SelectedStudentTargetCompletionReportForm';
import '../AllUsers/Users.css';
import { AwardLogEntry } from 'Src/helpers/service/admin/awards';
import { GrantAwardModal } from 'Src/components/GrantAwardModal/GrantAwardModal';
import { ModuleUpsellModal } from 'Src/components/ModuleUpsellModal/ModuleUpsellModal';
import { isModuleActiveFresh } from 'Src/helpers/modules/moduleHelpers';
import { createAwardType } from 'Src/helpers/service/admin/awardTypes';
import { getSchoolAwardTypes } from 'Src/helpers/service/admin/awards';
import { AwardType } from 'Src/helpers/service/admin/awards';
import { getUserTagColor } from 'Src/helpers/tags/tagColors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAward,
  faCheck,
  faEnvelope,
  faExclamationTriangle,
  faFileAlt,
  faGraduationCap,
  faLock,
  faStar,
  faTrash,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { ListEmptyState } from 'Src/components/ListEmptyState/ListEmptyState';
import './Students.scss';
import { countActivePeopleFilters, PeopleFilterChips } from '../PeopleFilterChips';
import { PeopleFilterChipOption, PeopleFilterChipSelect } from '../PeopleFilterChipSelect';
import { SearchMultiSelectFilter } from 'Src/components/SearchMultiSelectFilter/SearchMultiSelectFilter';
import { DatePicker as SitDatePicker } from 'Src/components/DatePicker/DatePicker';
import { Pagination2 } from 'Src/components/Pagination/Pagination2';
import { PeopleListPageHeader } from '../PeopleListPageHeader';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'firstName',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['firstName']
  },
  {
    text: 'Surname',
    field: 'lastName',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['lastName']
  },
  {
    text: 'Form',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['form', 'name']
  },
  {
    text: 'Age',
    field: 'ages',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['form', 'ageGroup']
  },
  {
    text: 'House',
    field: 'studentHouseName',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getStudentHouses
  },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getGender
  },
  {
    text: 'Birthday',
    field: 'birthday',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getBirthday
  },
  {
    text: 'SEN',
    field: 'specialEducationNeeds',
    isSort: false,
    type: FILTER_TYPE.SELECT,
    accessor: getStudentSpecialEducationNeeds
  },
  {
    text: 'PP',
    field: 'pupilsPremium',
    isSort: false,
    type: FILTER_TYPE.SELECT,
    accessor: getStudentPupilsPremium
  },
  {
    text: 'FSM',
    field: 'freeSchoolMeals',
    isSort: false,
    type: FILTER_TYPE.SELECT,
    accessor: getStudentFreeSchoolMeals
  },
  {
    text: 'Tag',
    field: 'tag',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getStudentTags
  },
  {
    text: 'Parents',
    field: 'parents',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: getStudentsParents
  }
];
const FILTER_COLUMNS: ColumnDefinition[] = [
  ...COLUMNS,
  { text: 'Name', field: 'nameSearch', isSort: false, type: FILTER_TYPE.TEXT, accessor: ['firstName'] },
  { text: 'No reg form', field: 'noRegForm', isSort: false, type: FILTER_TYPE.SELECT, accessor: [] },
  { text: 'No house', field: 'noHouse', isSort: false, type: FILTER_TYPE.SELECT, accessor: [] },
  { text: 'No DOB', field: 'noDob', isSort: false, type: FILTER_TYPE.SELECT, accessor: [] },
  {
    text: 'Parents',
    field: 'hasParents',
    isSort: false,
    type: FILTER_TYPE.SELECT,
    accessor: []
  }
];

interface State {
  items: SchoolStudent[];
  currentPage: number;
  selectedItems: SchoolStudent[];
  previewStudent: SchoolStudent | null;
  similarStudents: SchoolStudent[];
  identicalStudents: SchoolStudent[];
  studentToAdd: SchoolStudent;
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  excludedSelectionIds: string[];
  total: number;
  basePath: string;

  isMessageModalOpen: boolean;

  isMessageAudienceModalOpen: boolean;

  isGeneralMessagesUpsellOpen: boolean;

  isGeneralMessagesAvailabilityChecking: boolean;
  isTagAssignModalOpen: boolean;
  isTagDeleteModalOpen: boolean;
  isBulkFormModalOpen: boolean;
  isBulkHouseModalOpen: boolean;
  selectedBulkFormId: string;
  selectedBulkHouseId: string;
  bulkStudentGroupSearch: string;
  selectedTagId: string;
  selectedTagIds: string[];

  currentTabIndex: number;
  isAddNextOfKinModalOpen: boolean;
  personIndex: number;
  isEditStudent: boolean;
  isEditStudentModalOpen: boolean;
  isCreateStudentsChain: boolean;
  isDeleteStudentModalOpen: boolean;
  deleteConfirm: string;
  isStudentParticipationInEventsReportModalOpen: boolean; //015
  isStudentParticipationStatisticsByActivitiesReportModalOpen: boolean; //071
  isClubListForStudentReportModalOpen: boolean; //019
  isSelectedStudentTargetCompletionReportModalOpen: boolean; //056
  isReportsSelectionModalOpen: boolean;

  errorMessage: string;
  isErrorModalOpen: boolean;

  tags: Tag[];
  forms: SchoolForm[];
  houses: any[];
  maxSizeUploadFile: number;
  isGrantAwardOpen: boolean;
  logs: AwardLogEntry[];
  currentAwardId: string;
  isAwardsModuleActive?: boolean;
  hiddenStudentColumnKeys: string[];
  isColumnsMenuOpen: boolean;
  isSelectionMenuOpen: boolean;
  messageRecipients: 'parents' | 'students' | 'both';
  tagFilterSearch: string;

  preselectedAwardType: AwardType | null;

  isNoHouseWarningOpen: boolean;
  isPartialHouseWarningOpen: boolean;
  studentsWithoutHouse: SchoolStudent[];
  grantAwardStudents: SchoolStudent[] | null;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class Students extends React.Component<Props, State> {
  private itemsRequestId = 0;

  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      previewStudent: null,
      similarStudents: [],
      identicalStudents: [],
      studentToAdd: undefined,
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      excludedSelectionIds: [],
      total: 0,
      basePath: '',

      isMessageModalOpen: false,

      isMessageAudienceModalOpen: false,

      isGeneralMessagesUpsellOpen: false,

      isGeneralMessagesAvailabilityChecking: false,
      isTagAssignModalOpen: false,
      isTagDeleteModalOpen: false,
      isBulkFormModalOpen: false,
      isBulkHouseModalOpen: false,
      selectedBulkFormId: '',
      selectedBulkHouseId: '',
      bulkStudentGroupSearch: '',
      selectedTagId: '',
      selectedTagIds: [],

      currentTabIndex: 0,
      isEditStudent: false,
      isEditStudentModalOpen: false,
      isCreateStudentsChain: false,
      isDeleteStudentModalOpen: false,
      deleteConfirm: '',
      isStudentParticipationInEventsReportModalOpen: false,
      isStudentParticipationStatisticsByActivitiesReportModalOpen: false,
      isClubListForStudentReportModalOpen: false,
      isSelectedStudentTargetCompletionReportModalOpen: false,
      isReportsSelectionModalOpen: false,

      errorMessage: '',
      isErrorModalOpen: false,

      tags: [],
      forms: [],
      houses: [],
      maxSizeUploadFile: 0,
      isAddNextOfKinModalOpen: false,
      personIndex: -1,
      isGrantAwardOpen: false,
      logs: [],
      currentAwardId: '',
      hiddenStudentColumnKeys: this.getInitialHiddenStudentColumnKeys(),
      isColumnsMenuOpen: false,
      isSelectionMenuOpen: false,
      messageRecipients: 'both',
      tagFilterSearch: '',

      preselectedAwardType: null,

      isNoHouseWarningOpen: false,
      isPartialHouseWarningOpen: false,
      studentsWithoutHouse: [],
      grantAwardStudents: null
    };
  }

  getStudentColumnsStorageKey(): string {
    const { user } = this.props;
    const schoolId = propz.get(user, ['activeSchoolId'], propz.get(user, ['activeSchool', 'id'], 'school'));
    const userId = propz.get(user, ['userId'], propz.get(user, ['id'], propz.get(user, ['profile', 'id'], 'user')));
    const role = propz.get(user, ['activeRole'], 'role');

    return `sit.students.hiddenColumns:${schoolId}:${userId}:${role}`;
  }

  getInitialHiddenStudentColumnKeys(): string[] {
    if (typeof window === 'undefined') {
      return [];
    }

    try {
      return JSON.parse(window.localStorage.getItem(this.getStudentColumnsStorageKey()) || '[]');
    } catch (error) {
      return [];
    }
  }

  persistHiddenStudentColumnKeys = (hiddenStudentColumnKeys: string[]): void => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(this.getStudentColumnsStorageKey(), JSON.stringify(hiddenStudentColumnKeys));
    }

    this.setState({ hiddenStudentColumnKeys });
  };

  toggleStudentColumn = (field: string): void => {
    const hidden = new Set(this.state.hiddenStudentColumnKeys);

    if (hidden.has(field)) {
      hidden.delete(field);
    } else {
      hidden.add(field);
    }

    this.persistHiddenStudentColumnKeys(Array.from(hidden));
  };

  resetStudentColumns = (): void => {
    this.persistHiddenStudentColumnKeys([]);
  };

  onStudentPreviewClick = (student: SchoolStudent): void => {
    this.setState({
      previewStudent: student,
      currentTabIndex: 0
    });
  };

  closeStudentPreview = (): void => {
    this.setState({
      previewStudent: null,
      currentTabIndex: 0
    });
  };

  usePreviewStudentForAction = (callback?: () => void): void => {
    const { previewStudent } = this.state;

    if (!previewStudent) {
      return;
    }

    this.setState(
      {
        selectedItems: [previewStudent]
      },
      callback
    );
  };

  onStudentRowCheckboxClick = (event: React.ChangeEvent<HTMLInputElement>, student: SchoolStudent): void => {
    event.stopPropagation();

    if (this.state.isSelectAllChecked) {
      const excludedSelectionIds = [...this.state.excludedSelectionIds];
      const excludedIndex = excludedSelectionIds.indexOf(student.id);
      if (excludedIndex === -1) excludedSelectionIds.push(student.id);
      else excludedSelectionIds.splice(excludedIndex, 1);
      this.setState({ excludedSelectionIds });
      return;
    }

    const selectedItems = [...this.state.selectedItems];
    const selectedItemIndex = selectedItems.findIndex(item => item.id === student.id);

    if (selectedItemIndex === -1) {
      selectedItems.push(student);
    } else {
      selectedItems.splice(selectedItemIndex, 1);
    }

    this.setState({ selectedItems });
  };

  onModernFilterChange = (field: string, value: string): void => {
    const filters = { ...this.state.filters };

    if (value === '') {
      delete filters[field];
    } else {
      const filter = FILTER_COLUMNS.find(col => col.field === field);
      filters[field] = filter && filter.type === FILTER_TYPE.MULTISELECT ? [value] : value;
    }

    if (field === 'forms') {
      delete filters.ages;
    }

    if (field === 'ages') {
      delete filters.forms;
    }

    this.setState({ filters });
  };

  onModernTagFilterToggle = (value: string): void => {
    const current = Array.isArray(this.state.filters.tag)
      ? this.state.filters.tag
      : this.state.filters.tag
      ? [this.state.filters.tag]
      : [];
    this.setState({
      filters: {
        ...this.state.filters,
        tag: current.indexOf(value) === -1 ? [...current, value] : current.filter(item => item !== value)
      }
    });
  };

  onModernMultiFilterToggle = (field: string, value: string): void => {
    const currentValue = this.state.filters[field];
    const current = Array.isArray(currentValue) ? currentValue.map(String) : currentValue ? [String(currentValue)] : [];
    const next = current.indexOf(value) === -1 ? [...current, value] : current.filter(item => item !== value);
    const filters = { ...this.state.filters, [field]: next };

    if (field === 'forms') {
      delete filters.ages;
    }
    if (field === 'ages') {
      delete filters.forms;
    }

    this.setState({ filters });
  };

  onBirthdayRangeChange = (value: any): void => {
    const range = value || { start: null, end: null };
    this.setState({
      filters: {
        ...this.state.filters,
        birthday: {
          from: range.start ? moment(range.start).format('DD-MM-YYYY') : '',
          to: range.end ? moment(range.end).format('DD-MM-YYYY') : ''
        }
      }
    });
  };

  onModernFilterKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.onApplyFilterClick();
    }
  };
  onDocumentClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;

    if (
      target &&
      target.closest &&
      (target.closest('.people-columns') ||
        target.closest('.people-filter') ||
        target.closest('.people-filterBtn') ||
        target.closest('.people-bulkSelect') ||
        target.closest('.sit-dp__pop'))
    ) {
      return;
    }

    const shouldRestoreFilters = this.state.isShowFilter;
    this.setState({ isColumnsMenuOpen: false, isShowFilter: false, isSelectionMenuOpen: false });
    if (shouldRestoreFilters) this.setItems();
  };
  onDocumentKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      const shouldRestoreFilters = this.state.isShowFilter;
      this.setState({ isColumnsMenuOpen: false, isShowFilter: false, isSelectionMenuOpen: false });
      if (shouldRestoreFilters) this.setItems();
    }
  };
  componentDidMount() {
    this.setState({
      isLoading: true
    });

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeyDown);

    this.setAdditionalItems()
      .catch(error => console.error('Unable to load student list filters', error))
      .then(() => this.setItems());
  }

  componentWillUnmount() {
    this.itemsRequestId += 1;
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeyDown);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true
      });
      this.setItems();
    }
  }

  setAdditionalItems() {
    const { user } = this.props;

    return BPromise.all([
      getAllHouses(user),
      getAllForms(user),
      getAllUserTags(user),
      getSettingsUploadFiles(user)
    ]).then(([houses, forms, tags, maxSizeUploadFile]) => {
      return new BPromise<boolean>(resolve => {
        this.setState(
          {
            houses,
            forms: forms,
            tags: tags,
            maxSizeUploadFile: maxSizeUploadFile
          },
          () => resolve(true)
        );
      });
    });
  }

  setItems() {
    const { history, user } = this.props;

    const search = parse(history.location.search);
    const parsedPage = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;
    const page = Number.isInteger(parsedPage) && parsedPage > 0 ? parsedPage : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(FILTER_COLUMNS, search);

    const isShowFilter = false;
    const isDataFiltered = isFilterExist2(filters);

    const options = {
      forms: this.state.forms
    };

    const where = getServerFieldSectionWhere(filters, options);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const requestId = ++this.itemsRequestId;
    this.setState({ isLoading: true });

    return searchSchoolStudents(user, serverQueryFilter)
      .then(({ items, total }) => {
        if (requestId !== this.itemsRequestId) return false;
        const lastPage = Math.max(FIRST_PAGE, Math.ceil(total / LIMIT));
        if (page > lastPage) {
          const nextSearch = { ...search };
          if (lastPage === FIRST_PAGE) delete nextSearch.page;
          else nextSearch.page = String(lastPage);
          history.replace({ pathname: history.location.pathname, search: stringify(nextSearch) });
          return false;
        }
        this.setState({
          currentPage: page,
          sortDirection: sortDirection,
          sortColumnsName: sortColumnsName,
          isShowFilter: false,
          isDataFiltered: isDataFiltered,
          filters: filters,
          items: items,
          previewStudent: this.state.previewStudent
            ? items.find(item => item.id === this.state.previewStudent.id) || null
            : null,
          total,
          basePath: history.location.pathname,
          isLoading: false
        });

        return true;
      })
      .catch(error => {
        if (requestId === this.itemsRequestId) this.setState({ isLoading: false });
        console.error('Unable to load students', error);
        return false;
      });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: false,
      excludedSelectionIds: []
    });
  };

  onSelectAllClick = (): void => {
    this.setState({
      isSelectAllChecked: true,
      selectedItems: [],
      excludedSelectionIds: [],
      isSelectionMenuOpen: false
    });
  };

  resolveSelectedStudentsForBulkAction = (callback: () => void): void => {
    const { isSelectAllChecked, excludedSelectionIds, filters, forms } = this.state;

    if (!isSelectAllChecked) {
      callback();
      return;
    }

    this.setState({ isLoading: true });

    const user = this.props.user;
    const options = { forms };
    const where = getServerFieldSectionWhere(filters, options);

    getAllSchoolStudents(user, where)
      .then(items => {
        const excludedIds = new Set(excludedSelectionIds);
        const nextSelectedItems = items.filter(item => !excludedIds.has(item.id));

        this.setState(
          {
            selectedItems: nextSelectedItems,
            isLoading: false
          },
          callback
        );
      })
      .catch(error => {
        this.setState({ isLoading: false });
        console.error('Unable to resolve all selected students', error);
      });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      excludedSelectionIds: [],
      isSelectionMenuOpen: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (filterField === 'forms') {
      nextFilters.ages = [];
    }

    if (filterField === 'ages') {
      nextFilters.forms = [];
    }

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = FILTER_COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      excludedSelectionIds: [],
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      filters: {},
      isSelectAllChecked: false,
      selectedItems: [],
      excludedSelectionIds: [],
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter,
      isColumnsMenuOpen: false
    });
  };

  stylizeCell = cell => {
    const itemWithoutForm = propz.get(cell, ['rowData', 'formId'], '');
    const isHighliteRow = itemWithoutForm === '';
    return isHighliteRow ? 'text-danger' : '';
  };

  onSubmitReportForm = (data, reportType) => {
    const { user } = this.props;
    const { activeSchoolId, activeSchool } = user;
    const { selectedItems, forms } = this.state;
    const student = selectedItems[0];
    const studentId = propz.get(student, ['id'], '');

    const {
      dateTo,
      dateFrom,
      isShowSportEvents,
      isShowClubEvents,
      isShowTournamentEvents,
      isShowCompleted,
      isShowUncompleted,
      isPrintFormName,
      isPrintAgeGroup,
      isPrintHouse,
      isPrintPP,
      isPrintSEN,
      isPrintFSM,
      isPrintAttendance,
      isGroupBySports,
      isGroupByEventTypes,
      isShowZero,
      isShowNonZero
    } = data;

    let modData;
    let formsUniq;
    let ages: number[];

    switch (reportType) {
      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.STUDENT_PARTICIPATION_IN_EVENTS:
        this.setState({
          isStudentParticipationInEventsReportModalOpen: false,
          isLoading: true
        });

        dateFrom.setHours(0, 0, 0, 0);
        dateTo.setHours(23, 59, 59, 0);

        modData = {
          reportParamsCSV: {
            schoolId: activeSchoolId,
            studentId: selectedItems.map(item => item.id),
            dateTo: moment(dateTo).format('YYYY-MM-DD HH:mm:ss'),
            dateFrom: moment(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
            printPP: isPrintPP,
            printSEN: isPrintSEN,
            printFSM: isPrintFSM,
            printAttendance: isPrintAttendance,
            showSportEvents: isShowSportEvents,
            showClubEvents: isShowClubEvents,
            showTournamentEvents: isShowTournamentEvents
          }
        };
        break;

      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.STUDENT_PARTICIPATION_STATISTICS_BY_ACTIVITIES:
        this.setState({
          isStudentParticipationStatisticsByActivitiesReportModalOpen: false,
          isLoading: true
        });

        dateFrom.setHours(0, 0, 0, 0);
        dateTo.setHours(23, 59, 59, 0);

        modData = {
          reportParamsCSV: {
            schoolId: activeSchoolId,
            studentId: selectedItems.map(item => item.id),
            dateTo: moment(dateTo).format('YYYY-MM-DD HH:mm:ss'),
            dateFrom: moment(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
            printPP: isPrintPP,
            printSEN: isPrintSEN,
            printFSM: isPrintFSM,
            showClubEvents: isShowClubEvents,
            groupBySports: isGroupBySports,
            groupByEventTypes: isGroupByEventTypes,
            showZero: isShowZero,
            showNonZero: isShowNonZero
          }
        };
        break;

      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.CLUB_LIST_FOR_STUDENT:
        this.setState({
          isClubListForStudentReportModalOpen: false,
          isLoading: true
        });

        dateFrom.setHours(0, 0, 0, 0);
        dateTo.setHours(23, 59, 59, 0);

        modData = {
          reportParamsCSV: {
            schoolId: activeSchoolId,
            studentId: selectedItems.map(item => item.id),
            dateTo: moment(dateTo).format('YYYY-MM-DD HH:mm:ss'),
            dateFrom: moment(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
            printFormName: isPrintFormName,
            printAgeGroup: isPrintAgeGroup,
            printHouse: isPrintHouse,
            printPP: isPrintPP,
            printSEN: isPrintSEN,
            printFSM: isPrintFSM,
            printAttendance: isPrintAttendance
          }
        };
        break;

      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.SELECTED_STUDENT_TARGET_COMPLETION:
        this.setState({
          isSelectedStudentTargetCompletionReportModalOpen: false,
          isLoading: true
        });

        dateFrom.setHours(0, 0, 0, 0);
        dateTo.setHours(23, 59, 59, 0);

        modData = {
          reportParamsCSV: {
            schoolId: activeSchoolId,
            studentId: selectedItems.map(item => item.id),
            dateTo: moment(dateTo).format('YYYY-MM-DD HH:mm:ss'),
            dateFrom: moment(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
            showCompleted: isShowCompleted,
            showUncompleted: isShowUncompleted
          }
        };
        break;
    }

    uploadReport(user, reportType, modData)
      .then(data => {
        if (typeof data.reportId !== 'undefined') {
          const { reportType } = data;

          this.setState({
            isLoading: false
          });

          const url = `${window.apiBase}/public/report/${data.reportId}/reportType/${reportType}`;
          window.open(url);
        } else {
          this.setState({
            isLoading: false,
            errorMessage: 'No read report',
            isErrorModalOpen: true
          });
        }
      })
      .catch(error => {
        const errorText = propz.get(error, ['response', 'data', 'details', 'text'], '');
        console.error(error);

        this.setState({
          isLoading: false,
          errorMessage: errorText,
          isErrorModalOpen: true
        });
      });
  };

  openStudentParticipationInEventsReportForm = () => {
    this.resolveSelectedStudentsForBulkAction(() => {
      this.setState({
        isStudentParticipationInEventsReportModalOpen: true
      });
    });
  };

  closeStudentParticipationInEventsReportForm = () => {
    this.setState({
      isStudentParticipationInEventsReportModalOpen: false
    });
  };

  openStudentParticipationStatisticsByActivitiesReportForm = () => {
    this.resolveSelectedStudentsForBulkAction(() => {
      this.setState({
        isStudentParticipationStatisticsByActivitiesReportModalOpen: true
      });
    });
  };

  closeStudentParticipationStatisticsByActivitiesReportForm = () => {
    this.setState({
      isStudentParticipationStatisticsByActivitiesReportModalOpen: false
    });
  };

  openClubListForStudentReportForm = () => {
    this.resolveSelectedStudentsForBulkAction(() => {
      this.setState({
        isClubListForStudentReportModalOpen: true
      });
    });
  };

  closeClubListForStudentReportForm = () => {
    this.setState({
      isClubListForStudentReportModalOpen: false
    });
  };

  openSelectedStudentTargetCompletionReportForm = () => {
    this.resolveSelectedStudentsForBulkAction(() => {
      this.setState({
        isSelectedStudentTargetCompletionReportModalOpen: true
      });
    });
  };

  closeSelectedStudentTargetCompletionReportForm = () => {
    this.setState({
      isSelectedStudentTargetCompletionReportModalOpen: false
    });
  };

  openReportsSelectionModal = (): void => {
    this.resolveSelectedStudentsForBulkAction(() => this.setState({ isReportsSelectionModalOpen: true }));
  };

  closeReportsSelectionModal = (): void => this.setState({ isReportsSelectionModalOpen: false });

  selectReportOption = (action: () => void): void => {
    this.setState({ isReportsSelectionModalOpen: false }, action);
  };

  renderReportsSelectionModal(): React.ReactNode {
    const { isReportsSelectionModalOpen, selectedItems } = this.state;
    const reports = [
      {
        name: 'Student participation in events',
        description: 'Events and attendance for the selected students.',
        formats: 'Excel',
        action: this.openStudentParticipationInEventsReportForm
      },
      {
        name: 'Participation statistics by sport / activity',
        description: 'Participation totals grouped by activity for the selected students.',
        formats: 'Excel',
        action: this.openStudentParticipationStatisticsByActivitiesReportForm
      },
      ...(selectedItems.length === 1
        ? [
            {
              name: 'Club list for student',
              description: 'Clubs associated with the selected student.',
              formats: 'Excel',
              action: this.openClubListForStudentReportForm
            },
            {
              name: 'Target completion for the period',
              description: 'Target completion for the selected reporting period.',
              formats: 'Excel',
              action: this.openSelectedStudentTargetCompletionReportForm
            }
          ]
        : [])
    ];
    return (
      <SimpleModal
        customClass="eEventPrintModal"
        isOpen={isReportsSelectionModalOpen}
        title="Printed forms & reports"
        onCloseClick={this.closeReportsSelectionModal}
        showFooter={false}
        isBodyBold={false}
      >
        <div className="eEventPrintModal__intro">
          Choose a report. You can configure its contents and format on the next step.
        </div>
        <section className="eEventPrintModal__group">
          <h3 className="eEventPrintModal__groupTitle">Student reports</h3>
          <div className="eEventPrintModal__list">
            {reports.map(report => (
              <button
                type="button"
                className="eEventPrintModal__item"
                key={report.name}
                onClick={() => this.selectReportOption(report.action)}
              >
                <span className="eEventPrintModal__itemIcon" aria-hidden="true">
                  <FontAwesomeIcon icon={faFileAlt} />
                </span>
                <span className="eEventPrintModal__itemCopy">
                  <span className="eEventPrintModal__itemHeading">
                    <span>{report.name}</span>
                    <span className="eEventPrintModal__format">{report.formats}</span>
                  </span>
                  <span className="eEventPrintModal__itemDescription">{report.description}</span>
                </span>
                <span className="eEventPrintModal__arrow" aria-hidden="true">
                  ›
                </span>
              </button>
            ))}
          </div>
        </section>
      </SimpleModal>
    );
  }

  renderReportModalTitle = (title: string) => (
    <div className="eReportModalTitle">
      <span className="eReportModalTitle__icon" aria-hidden="true">
        <FontAwesomeIcon icon={faFileAlt} />
      </span>
      <span className="eReportModalTitle__copy">
        <span className="eReportModalTitle__name">{title}</span>
        <span className="eReportModalTitle__category">Student report</span>
      </span>
    </div>
  );

  renderReportDrawer(isOpen: boolean, title: string, onClose: () => void, children: React.ReactNode) {
    return (
      <SimpleModal
        customClass="eEventReportParametersModal eStudentReportParametersModal"
        bodyClassName="eEventReportParametersModal__body"
        isOpen={isOpen}
        title={this.renderReportModalTitle(title)}
        showFooter={false}
        isBodyBold={false}
        onCloseClick={onClose}
      >
        {children}
      </SimpleModal>
    );
  }

  renderStudentParticipationInEventsReportFormModal() {
    const { isStudentParticipationInEventsReportModalOpen, selectedItems } = this.state;
    const { user } = this.props;
    const activeSchoolId = user && user.activeSchool ? user.activeSchool.id : null;
    return this.renderReportDrawer(
      isStudentParticipationInEventsReportModalOpen,
      'Student participation in events',
      this.closeStudentParticipationInEventsReportForm,
      <StudentParticipationInEventsReportForm
        onCloseClick={this.closeStudentParticipationInEventsReportForm}
        onSubmit={this.onSubmitReportForm}
        student={selectedItems}
        activeSchoolId={activeSchoolId}
      />
    );
  }

  renderStudentParticipationStatisticsByActivitiesReportFormModal() {
    const { isStudentParticipationStatisticsByActivitiesReportModalOpen, selectedItems } = this.state;
    const { user } = this.props;
    const activeSchoolId = user && user.activeSchool ? user.activeSchool.id : null;
    return this.renderReportDrawer(
      isStudentParticipationStatisticsByActivitiesReportModalOpen,
      'Participation statistics by sport / activity',
      this.closeStudentParticipationStatisticsByActivitiesReportForm,
      <StudentParticipationStatisticsByActivitiesReportForm
        onCloseClick={this.closeStudentParticipationStatisticsByActivitiesReportForm}
        onSubmit={this.onSubmitReportForm}
        student={selectedItems}
        activeSchoolId={activeSchoolId}
      />
    );
  }

  renderClubListForStudentReportFormModal() {
    const { isClubListForStudentReportModalOpen, selectedItems } = this.state;
    const { user } = this.props;
    const activeSchoolId = user && user.activeSchool ? user.activeSchool.id : null;
    return this.renderReportDrawer(
      isClubListForStudentReportModalOpen,
      'Club list for student',
      this.closeClubListForStudentReportForm,
      <ClubListForStudentReportForm
        onCloseClick={this.closeClubListForStudentReportForm}
        onSubmit={this.onSubmitReportForm}
        student={selectedItems}
        activeSchoolId={activeSchoolId}
      />
    );
  }

  renderSelectedStudentTargetCompletionReportFormModal() {
    const { isSelectedStudentTargetCompletionReportModalOpen } = this.state;
    return this.renderReportDrawer(
      isSelectedStudentTargetCompletionReportModalOpen,
      'Target completion for the period',
      this.closeSelectedStudentTargetCompletionReportForm,
      <SelectedStudentTargetCompletionReportForm
        onCloseClick={this.closeSelectedStudentTargetCompletionReportForm}
        onSubmit={this.onSubmitReportForm}
      />
    );
  }
  getHouseId = (s: any): string | null => (s?.houseId ?? s?.house?.id ?? null) || null;

  renderStudentsTable(): React.ReactNode {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      isSelectAllChecked,
      excludedSelectionIds,
      previewStudent,
      filters,
      currentPage,
      houses,
      total,
      tags,
      forms,
      hiddenStudentColumnKeys,
      isColumnsMenuOpen,
      isSelectionMenuOpen
    } = this.state;

    const user = this.props.user;
    const activeSchool = user.activeSchool;
    const ageGroupsNaming = activeSchool.ageGroupsNaming;
    const hiddenColumns = new Set(hiddenStudentColumnKeys);
    const selectedIds = new Set(selectedItems.map(item => item.id));
    const excludedIds = new Set(excludedSelectionIds);
    const previewStudentId = previewStudent ? previewStudent.id : '';
    const allPageSelected = isSelectAllChecked
      ? items.length > 0 && items.every(item => !excludedIds.has(item.id))
      : items.length > 0 && items.every(item => selectedIds.has(item.id));
    const selectedCount = isSelectAllChecked ? Math.max(0, total - excludedSelectionIds.length) : selectedItems.length;
    const filterOptions = {
      studentHouseName: getSelectOptionForHouses(houses),
      ages: getSelectOptionForAge(forms, ageGroupsNaming),
      gender: getSelectOptionForGender(),
      tag: getSelectOptionForTag(tags),
      forms: getSelectOptionForForms(forms),
      specialEducationNeeds: getSelectOptionForBooleanQuestion(),
      pupilsPremium: getSelectOptionForBooleanQuestion(),
      freeSchoolMeals: getSelectOptionForBooleanQuestion()
    };

    const lockedColumns = new Set(['firstName', 'lastName']);
    const visibleColumns = COLUMNS.filter(
      column => lockedColumns.has(column.field) || !hiddenColumns.has(column.field)
    );

    const getFilterValue = (field: string): string => {
      const value = filters[field];
      if (Array.isArray(value)) {
        return value[0] || '';
      }
      if (value && typeof value === 'object') {
        return '';
      }
      return typeof value === 'undefined' ? '' : String(value);
    };

    const renderSelect = (field: string, label: string, options: any[], placeholder: string): React.ReactNode => (
      <label className="people-filter__field">
        <span>{label}</span>
        <select
          className={
            getFilterValue(field) === ''
              ? 'students-filter__select students-filter__select--placeholder'
              : 'students-filter__select'
          }
          value={getFilterValue(field)}
          onChange={event => this.onModernFilterChange(field, event.target.value)}
          onKeyDown={this.onModernFilterKeyDown}
        >
          <option value="">{placeholder}</option>
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.text || option.label || option.value}
            </option>
          ))}
        </select>
      </label>
    );

    const renderTagMultiSelect = (): React.ReactNode => {
      const selected = Array.isArray(filters.tag) ? filters.tag : filters.tag ? [filters.tag] : [];
      const tagById = new Map(tags.map(tag => [String(tag.id), tag]));
      return (
        <PeopleFilterChipSelect
          label="Tags"
          selected={selected.map(String)}
          collapsedLimit={6}
          options={filterOptions.tag.map(option => ({
            value: String(option.value),
            label: String(option.text || option.value),
            color: getUserTagColor(tagById.get(String(option.value)))
          }))}
          onToggle={value => this.onModernTagFilterToggle(value)}
        />
      );
    };

    const renderDirectoryChipMultiSelect = (
      field: 'forms' | 'studentHouseName',
      label: string,
      options: any[]
    ): React.ReactNode => {
      const current = filters[field];
      const selected = Array.isArray(current) ? current.map(String) : current ? [String(current)] : [];
      const formById = new Map(forms.map(form => [String(form.id), form]));
      const stageForAge = (age: number): Pick<PeopleFilterChipOption, 'group' | 'groupHint' | 'order'> => {
        if (age === 0) return { group: 'EYFS', groupHint: 'Reception', order: 0 };
        if (age <= 2) return { group: 'KS1', groupHint: 'Y1–Y2', order: 1 };
        if (age <= 6) return { group: 'KS2', groupHint: 'Y3–Y6', order: 2 };
        if (age <= 9) return { group: 'KS3', groupHint: 'Y7–Y9', order: 3 };
        if (age <= 11) return { group: 'KS4', groupHint: 'Y10–Y11', order: 4 };
        if (age <= 13) return { group: 'KS5', groupHint: 'Y12–Y13', order: 5 };
        return { group: 'Other', groupHint: '', order: 6 };
      };
      return (
        <PeopleFilterChipSelect
          label={label}
          selected={selected}
          collapsedLimit={field === 'studentHouseName' ? 6 : undefined}
          options={options.map(option => {
            const value = String(option.value);
            const form = formById.get(value);
            return {
              value,
              label: String(option.text || option.label || value),
              ...(field === 'forms' && form ? stageForAge(Number(form.age)) : {})
            };
          })}
          onToggle={value => this.onModernMultiFilterToggle(field, value)}
        />
      );
    };

    const renderBooleanSegments = (field: string, label: string, yesLabel = 'Yes', noLabel = 'No'): React.ReactNode => {
      const value = getFilterValue(field);
      return (
        <fieldset className="students-filter__segments">
          <legend>{label}</legend>
          <div>
            {[
              { value: '', label: 'Any' },
              { value: field === 'hasParents' ? 'yes' : 'true', label: yesLabel },
              { value: field === 'hasParents' ? 'no' : 'false', label: noLabel }
            ].map(option => (
              <button
                type="button"
                key={option.value || 'any'}
                className={value === option.value ? 'is-selected' : ''}
                onClick={() => this.onModernFilterChange(field, option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </fieldset>
      );
    };

    const renderChipMultiSelect = (field: string, label: string, options: any[]): React.ReactNode => {
      const currentValue = filters[field];
      const selected = Array.isArray(currentValue)
        ? currentValue.map(String)
        : currentValue
        ? [String(currentValue)]
        : [];

      return (
        <fieldset className="students-filter__chipField">
          <legend>{label}</legend>
          <div className="students-filter__choiceChips">
            {options.map(option => {
              const value = String(option.value);
              const isSelected = selected.indexOf(value) !== -1;
              return (
                <button
                  type="button"
                  key={value}
                  className={isSelected ? 'is-selected' : ''}
                  aria-pressed={isSelected}
                  onClick={() => this.onModernMultiFilterToggle(field, value)}
                >
                  {option.text || option.label || value}
                </button>
              );
            })}
          </div>
        </fieldset>
      );
    };

    const birthdayFilter = filters.birthday || {};
    const birthdayRange = {
      start: birthdayFilter.from ? moment(birthdayFilter.from, 'DD-MM-YYYY').toDate() : null,
      end: birthdayFilter.to ? moment(birthdayFilter.to, 'DD-MM-YYYY').toDate() : null
    };

    const renderSearchField = (field: string, label: string, placeholder: string): React.ReactNode => (
      <label className="people-search__field">
        <span>{label}</span>
        <input
          value={getFilterValue(field)}
          onChange={event => this.onModernFilterChange(field, event.target.value)}
          onKeyDown={this.onModernFilterKeyDown}
          placeholder={placeholder}
        />
      </label>
    );

    const getCellValue = (student: SchoolStudent, column: ColumnDefinition): React.ReactNode => {
      const field = column.field;

      switch (field) {
        case 'firstName':
          return (
            <div className="people-person">
              <span className="people-avatar">{`${propz.get(student, ['firstName'], '?').charAt(0)}${propz
                .get(student, ['lastName'], '')
                .charAt(0)}`}</span>
              <span className="people-person__name">{propz.get(student, ['firstName'], '')}</span>
            </div>
          );
        case 'lastName':
          return <span className="people-strong">{propz.get(student, ['lastName'], '')}</span>;
        case 'studentHouseName':
          return this.renderStudentHouse(student, houses);
        case 'specialEducationNeeds':
          return this.renderStudentFlag(propz.get(student, ['specialEducationNeeds']), 'sen', 'SEN');
        case 'pupilsPremium':
          return this.renderStudentFlag(propz.get(student, ['pupilsPremium']), 'pp', 'PP');
        case 'freeSchoolMeals':
          return this.renderStudentFlag(propz.get(student, ['freeSchoolMeals']), 'fsm', 'FSM');
        case 'tag':
          return this.renderStudentTags(student);
        case 'birthday':
          return this.formatStudentBirthday(student) || '-';
        case 'parents':
          return <span className="people-mutedText">{getStudentsParents(student) || '-'}</span>;
        default:
          const accessor = (column as any).accessor;
          if (typeof accessor === 'function') {
            return accessor(student);
          }
          if (Array.isArray(accessor)) {
            return propz.get(student, accessor, '-') || '-';
          }
          return propz.get(student, [field], '-') || '-';
      }
    };

    const toggleAllOnPage = (): void => {
      if (allPageSelected) {
        const pageIds = new Set(items.map(item => item.id));
        if (isSelectAllChecked) {
          this.setState({
            excludedSelectionIds: Array.from(new Set([...excludedSelectionIds, ...Array.from(pageIds)])),
            isSelectionMenuOpen: false
          });
          return;
        }
        this.setState({
          selectedItems: selectedItems.filter(item => !pageIds.has(item.id)),
          isSelectAllChecked: false,
          isSelectionMenuOpen: false
        });
      } else {
        if (isSelectAllChecked) {
          this.setState({
            excludedSelectionIds: excludedSelectionIds.filter(id => !items.some(item => item.id === id)),
            isSelectionMenuOpen: false
          });
          return;
        }
        const next = [...selectedItems];
        items.forEach(item => {
          if (!selectedIds.has(item.id)) {
            next.push(item);
          }
        });
        this.setState({ selectedItems: next, isSelectAllChecked: false, isSelectionMenuOpen: false });
      }
    };

    return (
      <div className="people-screen schoolPeopleListCard">
        <div className="people-toolbar">
          <div className="people-toolbar__heading">
            <div className="people-search people-search--name">
              {renderSearchField('nameSearch', 'Name', 'Search by first or last name')}
            </div>
            <button
              type="button"
              className={`people-btn people-btn--secondary people-btn--iconLeft people-filterBtn ${
                isShowFilter ? 'people-btn--on' : ''
              }`}
              onClick={this.onTableFilterClick}
            >
              <span className="people-icon people-icon--filter" />
              Filters
              {countActivePeopleFilters(filters) > 0 ? (
                <span className="people-btn__badge">{countActivePeopleFilters(filters)}</span>
              ) : null}
            </button>
            <div className="people-columns">
              <button
                type="button"
                className="people-btn people-btn--secondary people-btn--iconLeft"
                onClick={() => this.setState({ isColumnsMenuOpen: !isColumnsMenuOpen })}
              >
                <span className="people-icon people-icon--columns" />
                Columns <span className="people-btn__badge">{visibleColumns.length}</span>
              </button>
              {isColumnsMenuOpen && (
                <div className="people-columns__menu">
                  <div className="people-columns__head">
                    <span>Show / hide columns</span>
                    <button type="button" onClick={this.resetStudentColumns}>
                      Reset
                    </button>
                  </div>
                  {COLUMNS.map(column => {
                    const isLocked = lockedColumns.has(column.field);
                    const isVisible = isLocked || !hiddenColumns.has(column.field);
                    return (
                      <label
                        key={column.field}
                        className={`people-columns__item ${isLocked ? 'people-columns__item--locked' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isVisible}
                          disabled={isLocked}
                          onChange={() => this.toggleStudentColumn(column.field)}
                        />
                        <span>{column.text}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="people-toolbar__spacer" />
            {this.props.user.activeRole === 'ADMIN' ? (
              <React.Fragment>
                {this.props.user.activeSchool?.studentImportForAdminAllowed ? (
                  <button
                    type="button"
                    className="people-btn people-btn--secondary"
                    onClick={() => this.props.history.push('/student-age-group-check')}
                  >
                    Check age groups
                  </button>
                ) : null}
                <button
                  type="button"
                  className="people-btn people-btn--secondary"
                  onClick={() => this.props.history.push('/import-students')}
                >
                  Import
                </button>
              </React.Fragment>
            ) : null}
            <button
              type="button"
              className="people-btn people-btn--primary"
              disabled={selectedCount > 0}
              title={selectedCount > 0 ? 'Clear the current selection before adding a student.' : undefined}
              onClick={this.onStudentAddButtonClick}
            >
              <span aria-hidden="true">+</span> Add student
            </button>
          </div>
        </div>
        {isShowFilter && (
          <form
            className="people-filter students-filter"
            onSubmit={event => {
              event.preventDefault();
              this.onApplyFilterClick();
            }}
          >
            <div className="people-filter__header">
              <strong>Filters</strong>
              <button
                type="button"
                onClick={() => {
                  this.setState({ isShowFilter: false });
                  this.setItems();
                }}
                aria-label="Close filters"
              >
                ×
              </button>
            </div>
            <div className="people-filter__body">
              <SearchMultiSelectFilter
                label="Registration forms"
                selected={
                  Array.isArray(filters.forms)
                    ? filters.forms.map(String)
                    : filters.forms
                    ? [String(filters.forms)]
                    : []
                }
                options={filterOptions.forms.map(option => ({
                  value: String(option.value),
                  label: String(option.text || option.value)
                }))}
                onToggle={value => this.onModernMultiFilterToggle('forms', value)}
                placeholder="Search registration forms…"
              />
              {renderChipMultiSelect('ages', 'Age', filterOptions.ages)}
              {renderDirectoryChipMultiSelect('studentHouseName', 'Houses', filterOptions.studentHouseName)}
              <PeopleFilterChipSelect
                label="Missing information"
                selected={['noRegForm', 'noHouse', 'noDob'].filter(field => filters[field] === 'true')}
                options={[
                  { value: 'noRegForm', label: 'No reg form' },
                  { value: 'noHouse', label: 'No house' },
                  { value: 'noDob', label: 'No DOB' }
                ]}
                onToggle={field => this.onModernFilterChange(field, filters[field] === 'true' ? '' : 'true')}
              />
              {renderChipMultiSelect('gender', 'Gender', filterOptions.gender)}
              <div className="people-filter__dateRange">
                <SitDatePicker
                  label="DOB"
                  mode="range"
                  value={birthdayRange}
                  onChange={this.onBirthdayRangeChange}
                  format="DD-MM-YYYY"
                  placeholder="Select date range"
                  numberOfMonths={2}
                  align="right"
                  clearable={true}
                  syncRangeOnSelect={true}
                  className="students-filter__dob"
                />
              </div>
              {renderBooleanSegments('specialEducationNeeds', 'SEN')}
              {renderBooleanSegments('pupilsPremium', 'PP')}
              {renderBooleanSegments('freeSchoolMeals', 'FSM')}
              {renderBooleanSegments('hasParents', 'Parents linked', 'With parents', 'No parents')}
              {renderTagMultiSelect()}
            </div>
            <div className="people-filter__actions">
              <button type="button" className="people-btn people-btn--ghost" onClick={this.onClearFilterClick}>
                Clear all
              </button>
              <div>
                <button
                  type="button"
                  className="people-btn people-btn--secondary"
                  onClick={() => {
                    this.setState({ isShowFilter: false });
                    this.setItems();
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="people-btn people-btn--primary">
                  Apply
                </button>
              </div>
            </div>
          </form>
        )}

        <PeopleFilterChips
          filters={filters}
          columns={FILTER_COLUMNS}
          valueLabels={{
            forms: filterOptions.forms.reduce((result, option) => {
              result[String(option.value)] = option.text || String(option.value);
              return result;
            }, {}),
            ages: filterOptions.ages.reduce((result, option) => {
              result[String(option.value)] = option.text || String(option.value);
              return result;
            }, {}),
            gender: filterOptions.gender.reduce((result, option) => {
              result[String(option.value)] = option.text || String(option.value);
              return result;
            }, {}),
            studentHouseName: filterOptions.studentHouseName.reduce((result, option) => {
              result[String(option.value)] = option.text || String(option.value);
              return result;
            }, {}),
            hasParents: {
              yes: 'With parents',
              no: 'Without parents'
            },
            tag: filterOptions.tag.reduce((result, option) => {
              result[String(option.value)] = option.text || String(option.value);
              return result;
            }, {})
          }}
          onRemove={field => this.setState({ filters: { ...filters, [field]: '' } }, this.onApplyFilterClick)}
          onClear={this.onClearFilterClick}
        />

        {selectedCount > 0 && (
          <div className="people-selection">
            <span className="people-selection__summary">{selectedCount} selected</span>
            <div className="people-selection__actions">
              <button type="button" className="people-btn people-btn--secondary" onClick={this.onSendMessageClick}>
                <span aria-hidden="true">+</span> Create message
              </button>
              <button type="button" className="people-btn people-btn--secondary" onClick={this.onTagAssignClick}>
                Assign tag
              </button>
              <button type="button" className="people-btn people-btn--secondary" onClick={this.onTagDeleteClick}>
                Remove tag
              </button>
              <button type="button" className="people-btn people-btn--secondary" onClick={this.openBulkFormModal}>
                Add to reg form
              </button>
              <button type="button" className="people-btn people-btn--secondary" onClick={this.openBulkHouseModal}>
                Add to house
              </button>
              <div className="people-selection__reports">
                <button type="button" className="people-btn people-btn--secondary">
                  Awards
                </button>
                <div className="people-selection__menu">
                  <button type="button" onClick={this.openGrantAwardModal}>
                    <FontAwesomeIcon icon={faAward} /> Grant award
                  </button>
                  <button type="button" onClick={this.awardHousePoints}>
                    <FontAwesomeIcon icon={faStar} /> Award house points
                  </button>
                </div>
              </div>
              <button
                type="button"
                className="people-btn people-btn--secondary"
                onClick={this.openReportsSelectionModal}
              >
                <FontAwesomeIcon icon={faFileAlt} /> Reports
              </button>
              <button type="button" className="people-btn people-btn--danger" onClick={this.openDeleteStudentModal}>
                Delete students
              </button>
            </div>
            <div className="people-toolbar__spacer" />
            <button type="button" className="people-selection__clear" onClick={this.onUnselectAllClick}>
              Clear selection
            </button>
          </div>
        )}
        <div className="people-tableCard">
          <div className="people-tableScroll">
            <table className="people-table">
              <thead>
                <tr>
                  <th className="people-table__check">
                    <div className="people-bulkSelect">
                      <button
                        type="button"
                        className={`people-bulkSelect__trigger ${
                          allPageSelected ? 'people-bulkSelect__trigger--checked' : ''
                        }`}
                        aria-label="Bulk select students"
                        aria-expanded={isSelectionMenuOpen}
                        onClick={event => {
                          event.stopPropagation();
                          this.setState({ isSelectionMenuOpen: !isSelectionMenuOpen });
                        }}
                      >
                        <span className="people-bulkSelect__box" />
                      </button>
                      {isSelectionMenuOpen && (
                        <div className="people-bulkSelect__menu">
                          <button type="button" onClick={toggleAllOnPage}>
                            Select this page
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              this.onSelectAllClick();
                              this.setState({ isSelectionMenuOpen: false });
                            }}
                          >
                            Select all
                          </button>
                          <button
                            type="button"
                            disabled={selectedCount === 0}
                            onClick={() => {
                              this.onUnselectAllClick();
                              this.setState({ isSelectionMenuOpen: false });
                            }}
                          >
                            Clear selection
                          </button>
                        </div>
                      )}
                    </div>
                  </th>
                  {visibleColumns.map(column => {
                    const isSorted = sortColumnsName === column.field;
                    return (
                      <th
                        key={column.field}
                        onClick={column.isSort ? () => this.onTableSortClick(column.field) : undefined}
                        className={column.isSort ? 'people-table__sortable' : ''}
                      >
                        <span>{column.text}</span>
                        {column.isSort && (
                          <i className={`people-sort ${isSorted ? `people-sort--${sortDirection}` : ''}`} />
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td className="people-empty" colSpan={visibleColumns.length + 1}>
                      <ListEmptyState
                        icon={faGraduationCap}
                        title={isDataFiltered ? 'No students match your filters' : 'No students yet'}
                        description={
                          isDataFiltered
                            ? 'Try changing or clearing the search and filters to see more students.'
                            : 'Students added manually or through your school integration will appear here.'
                        }
                      />
                    </td>
                  </tr>
                ) : (
                  items.map(student => {
                    const isSelected = isSelectAllChecked ? !excludedIds.has(student.id) : selectedIds.has(student.id);
                    const isPreviewed = previewStudentId === student.id;
                    const isMissingForm = propz.get(student, ['formId'], '') === '';
                    return (
                      <tr
                        key={student.id}
                        className={isMissingForm ? 'people-table__row--warning' : ''}
                        data-selected={isSelected}
                        data-preview={isPreviewed}
                        onClick={() => this.onStudentPreviewClick(student)}
                      >
                        <td className="people-table__check" onClick={event => event.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={event => this.onStudentRowCheckboxClick(event, student)}
                          />
                        </td>
                        {visibleColumns.map(column => (
                          <td key={column.field}>{getCellValue(student, column)}</td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {total > 0 ? (
          <Pagination2
            modern
            currentPage={currentPage}
            total={total}
            setCurrentPageParams={this.setCurrentPageParams}
          />
        ) : null}
      </div>
    );
  }

  getStudentHouseColor(student: SchoolStudent, houses: any[]): string {
    const palette = ['#227ebd', '#f08c1a', '#2f9e5a', '#7b5bd6', '#d64545', '#0f8793', '#b45309', '#4f46e5'];
    const houseId = propz.get(student, ['houseId'], propz.get(student, ['house', 'id'], ''));
    const houseName = getStudentHouses(student) || propz.get(student, ['house', 'name'], '');
    const house =
      houses.find(item => item.id === houseId || item._id === houseId || item.name === houseName) ||
      propz.get(student, ['house'], {});
    const colors = propz.get(house, ['colors'], []);
    const color = Array.isArray(colors) && colors.length > 0 ? colors[0] : propz.get(house, ['color'], '');

    if (typeof color === 'string' && color.trim() !== '') {
      return color;
    }

    const seed = String(houseName || houseId || 'house')
      .split('')
      .reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return palette[seed % palette.length];
  }

  renderStudentHouse(student: SchoolStudent, houses: any[]): React.ReactNode {
    const houseName = getStudentHouses(student);

    if (!houseName) {
      return <span className="people-flag people-flag--off">-</span>;
    }

    const color = this.getStudentHouseColor(student, houses);
    return (
      <span className="people-house" style={{ '--house-color': color } as React.CSSProperties}>
        <i />
        <span>{houseName}</span>
      </span>
    );
  }
  renderStudentFlag(isOn: boolean | undefined | null, kind: 'sen' | 'pp' | 'fsm', label: string): React.ReactNode {
    if (typeof isOn !== 'boolean') return <span className="people-flag people-flag--unknown">—</span>;
    return (
      <span className={`people-flag ${isOn ? `people-flag--${kind}` : 'people-flag--no'}`}>{isOn ? 'Yes' : 'No'}</span>
    );
  }

  formatStudentBirthday(student: SchoolStudent): string {
    const birthday = propz.get(student, ['birthday'], '');
    if (!birthday) {
      return '';
    }

    const date = moment(birthday);
    return date.isValid() ? date.format('D MMM YYYY') : '';
  }
  renderStudentTags(student: SchoolStudent): React.ReactNode {
    const rawTags = Array.isArray(student.tags) ? student.tags : [];

    if (rawTags.length > 0) {
      return (
        <span className="people-tags">
          {rawTags.map(tag => {
            const color = getUserTagColor(tag);
            return (
              <span key={tag.id || tag.tagName} style={{ '--tag-color': color } as React.CSSProperties}>
                <i />
                {tag.tagName}
              </span>
            );
          })}
        </span>
      );
    }

    const tags = getStudentTags(student);

    if (!tags) {
      return <span className="people-flag people-flag--off">-</span>;
    }

    return (
      <span className="people-tags">
        {String(tags)
          .split(',')
          .map(tag => tag.trim())
          .filter(Boolean)
          .map(tag => (
            <span key={tag}>{tag}</span>
          ))}
      </span>
    );
  }
  onTagAssignClick = (): void => {
    this.resolveSelectedStudentsForBulkAction(() => {
      this.setState({
        isTagAssignModalOpen: true
      });
    });
  };

  onTagDeleteClick = (): void => {
    this.resolveSelectedStudentsForBulkAction(() => {
      this.setState({
        isTagDeleteModalOpen: true
      });
    });
  };

  onTagAssignModalCloseClick = (): void => {
    this.setState({
      isTagAssignModalOpen: false
    });
  };
  isGeneralMessagesEnabledForSchool(): boolean {
    const { user } = this.props;
    return Boolean(propz.get(user, ['activeSchool', 'isGeneralMessagesEnabled'], true));
  }

  isGeneralMessagesV2EnabledForSchool(): boolean {
    return true;
  }

  getGeneralMessagesVersionLabel(): string {
    return 'General Messages';
  }

  openGeneralMessagesUpsell = (): void => {
    this.setState({
      isGeneralMessagesUpsellOpen: true,
      isGeneralMessagesAvailabilityChecking: false
    });
  };

  closeGeneralMessagesUpsell = (): void => {
    this.setState({
      isGeneralMessagesUpsellOpen: false,
      isGeneralMessagesAvailabilityChecking: false
    });
  };

  openMessageAudienceModal = (messageRecipients: 'parents' | 'students' | 'both' = 'both'): void => {
    const hasLinkedParents = this.state.selectedItems.some(student => propz.get(student, ['parents'], []).length > 0);
    const availableRecipients = !hasLinkedParents && messageRecipients !== 'students' ? 'students' : messageRecipients;

    this.setState({
      isMessageAudienceModalOpen: true,
      messageRecipients: availableRecipients
    });
  };

  checkGeneralMessagesAccess = (onAllowed: () => void): void => {
    if (!this.isGeneralMessagesEnabledForSchool()) {
      this.openGeneralMessagesUpsell();
      return;
    }
    onAllowed();
  };

  onSendMessageClick = (): void => {
    this.resolveSelectedStudentsForBulkAction(() => {
      this.checkGeneralMessagesAccess(() => this.openMessageAudienceModal('both'));
    });
  };

  onSendMessageToParentsClick = (): void => {
    this.checkGeneralMessagesAccess(() => this.openMessageAudienceModal('parents'));
  };

  onSendMessageToStudentClick = (): void => {
    this.checkGeneralMessagesAccess(() => this.openMessageAudienceModal('students'));
  };

  onMessageAudienceContinueClick = (): void => {
    const { selectedItems, messageRecipients } = this.state;

    this.setState({
      isMessageAudienceModalOpen: false
    });

    this.props.history.push({
      pathname: '/generalMessages/create',
      state: {
        fromStudentsList: true,
        students: selectedItems,
        sendToStudents: messageRecipients === 'students' || messageRecipients === 'both',
        sendToParents: messageRecipients === 'parents' || messageRecipients === 'both',
        returnTo: {
          pathname: this.state.basePath,
          search: this.props.location.search
        }
      }
    });
  };

  onCloseMessageAudienceModalClick = (): void => {
    this.setState({
      isMessageAudienceModalOpen: false
    });
  };

  renderSummary(): React.ReactNode {
    const { currentTabIndex, previewStudent } = this.state;

    if (!previewStudent) {
      return null;
    }

    const student = previewStudent;
    const nextOfKin = propz.get(student, ['nextOfKin'], []);
    const hasLinkedParents = propz.get(student, ['parents'], []).length > 0;
    const fullName = getFullName(student);

    return (
      <>
        <div className="people-drawerScrim" onClick={this.closeStudentPreview} />
        <aside className="people-drawer" role="dialog" aria-modal="true">
          <div className="people-drawer__head">
            <span className="people-avatar people-avatar--lg">{`${propz
              .get(student, ['firstName'], '?')
              .charAt(0)}${propz.get(student, ['lastName'], '').charAt(0)}`}</span>
            <div className="people-drawer__identity">
              <h2>{fullName}</h2>
              <div>
                <span>Form {propz.get(student, ['form', 'name'], '-')}</span>
                <i />
                <span>{getStudentHouses(student) || 'No house'}</span>
                <i />
                <span>{this.formatStudentBirthday(student) || 'No birthday'}</span>
              </div>
            </div>
            <button
              type="button"
              className="people-drawer__close"
              onClick={this.closeStudentPreview}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="people-drawer__tabs">
            {STUDENT_TABS.map((tab, index) => (
              <button
                key={tab}
                type="button"
                className={currentTabIndex === index ? 'active' : ''}
                onClick={event => this.onTabClick(event, index)}
              >
                {tab}
                {index === 1 ? ` (${nextOfKin.length})` : ''}
              </button>
            ))}
          </div>

          <div className="people-drawer__body">{this.getStudentSummaryTabs()}</div>

          <div className="people-drawer__foot">
            {hasLinkedParents ? (
              <button
                type="button"
                className="people-btn people-btn--secondary"
                onClick={() => this.usePreviewStudentForAction(this.onSendMessageToParentsClick)}
              >
                Send message to parents
              </button>
            ) : null}
            <button
              type="button"
              className="people-btn people-btn--secondary"
              onClick={() => this.usePreviewStudentForAction(this.onSendMessageToStudentClick)}
            >
              Send message to student
            </button>
            {currentTabIndex === 1 ? (
              <button
                type="button"
                className="people-btn people-btn--secondary"
                onClick={() => this.usePreviewStudentForAction(this.onAddNextOfKinClick)}
              >
                <span aria-hidden="true">+</span> Add next of kin
              </button>
            ) : null}
            <button
              type="button"
              className="people-btn people-btn--primary"
              onClick={() => {
                this.usePreviewStudentForAction(() => {
                  this.setState({
                    isEditStudent: true,
                    isEditStudentModalOpen: true
                  });
                });
              }}
            >
              Edit student
            </button>
          </div>
        </aside>
      </>
    );
  }

  onStudentAddButtonClick = (): void => {
    if (this.state.isSelectAllChecked || this.state.selectedItems.length > 0) {
      return;
    }

    this.setState({
      isEditStudentModalOpen: true,
      isEditStudent: false,
      isCreateStudentsChain: false,
      studentToAdd: undefined,
      similarStudents: [],
      identicalStudents: []
    });
  };

  onTabClick = (event, index: number) => {
    event.preventDefault();

    this.setState({
      currentTabIndex: index
    });
  };

  onAddNextOfKinClick = (): void => {
    this.setState({
      isAddNextOfKinModalOpen: true
    });
  };

  onNextOfKinCancelButtonClick = (): void => {
    this.setState({
      isAddNextOfKinModalOpen: false,
      personIndex: -1
    });
  };

  renderAddNextOfKinModal(): React.ReactNode {
    const { isAddNextOfKinModalOpen, personIndex, selectedItems } = this.state;
    const user = this.props.user;
    const student = { ...selectedItems[0] };
    const nextOfKin = [...propz.get(student, ['nextOfKin'], [])];

    if (personIndex !== -1) {
      const person = nextOfKin[personIndex];

      return (
        <SimpleModal
          customClass="studentNextOfKinModal"
          isOpen={isAddNextOfKinModalOpen}
          title="Edit next of kin"
          showFooter={false}
          isBodyBold={false}
          onCloseClick={this.onNextOfKinCancelButtonClick}
        >
          <StudentNextOfKinForm
            onSubmit={this.onNextOfKinSaveButtonClick}
            onCancel={this.onNextOfKinCancelButtonClick}
            user={user}
            person={person}
          />
        </SimpleModal>
      );
    } else {
      return (
        <SimpleModal
          customClass="studentNextOfKinModal"
          isOpen={isAddNextOfKinModalOpen}
          title="Add next of kin"
          showFooter={false}
          isBodyBold={false}
          onCloseClick={this.onNextOfKinCancelButtonClick}
        >
          <StudentNextOfKinForm
            onSubmit={this.onNextOfKinSaveButtonClick}
            onCancel={this.onNextOfKinCancelButtonClick}
            user={user}
          />
        </SimpleModal>
      );
    }
  }

  onDeleteStudentsClick = (): void => {
    const { user } = this.props;
    const { selectedItems, deleteConfirm } = this.state;

    if (deleteConfirm.trim().toUpperCase() !== 'DELETE') {
      return;
    }

    this.setState({
      isLoading: true
    });

    removeStudentsInBulk(
      user,
      selectedItems.map(selectedItem => selectedItem.id)
    )
      .then(() => {
        this.setState({
          isDeleteStudentModalOpen: false,
          deleteConfirm: '',
          selectedItems: [],
          isSelectAllChecked: false,
          excludedSelectionIds: []
        });
        this.setItems();
      })
      .catch(error => {
        console.error('Can not remove students from the school', error);
        this.setState({ isLoading: false });
      });
  };

  renderDeleteStudentModal = (): React.ReactNode => {
    const { isDeleteStudentModalOpen, selectedItems, deleteConfirm } = this.state;
    const count = selectedItems.length;
    const selectedStudents = selectedItems.map(student => getFullName(student)).join(', ');
    const isConfirmed = deleteConfirm.trim().toUpperCase() === 'DELETE';

    return (
      <SimpleModal
        isOpen={isDeleteStudentModalOpen}
        title="Delete students"
        onCloseClick={this.closeDeleteStudentModal}
        showFooter={false}
        customClass="people-deleteModal"
        dialogStyle={{ maxWidth: 560 }}
        isBodyBold={false}
      >
        <div className="people-delete">
          <div className="people-delete__alert">
            <span className="people-delete__icon">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </span>
            <div>
              <h3>Remove selected students from this school?</h3>
              <p>
                You are about to revoke the active student role for {count} {count === 1 ? 'student' : 'students'} in
                this school.
              </p>
            </div>
          </div>

          <div className="people-delete__details">
            <p>
              Their user accounts and historical records are retained. They will disappear from the current student list
              and lose access through this school until a student role is restored.
            </p>
            {selectedStudents ? <p className="people-delete__names">Selected: {selectedStudents}</p> : null}
          </div>

          <label className="people-delete__confirm">
            <span>
              Type <strong>DELETE</strong> to confirm.
            </span>
            <input
              value={deleteConfirm}
              placeholder="DELETE"
              autoFocus
              onChange={event => this.setState({ deleteConfirm: event.target.value })}
              onKeyDown={event => {
                if (event.key === 'Enter' && isConfirmed) {
                  this.onDeleteStudentsClick();
                }
              }}
            />
          </label>

          <div className="people-delete__footer">
            <button type="button" className="sit-btn sit-btn--ghost" onClick={this.closeDeleteStudentModal}>
              Cancel
            </button>
            <button
              type="button"
              className="sit-btn sit-btn--danger"
              disabled={!isConfirmed}
              onClick={this.onDeleteStudentsClick}
            >
              <FontAwesomeIcon icon={faTrash} /> Remove {count} {count === 1 ? 'student' : 'students'}
            </button>
          </div>
        </div>
      </SimpleModal>
    );
  };

  openDeleteStudentModal = (): void => {
    this.resolveSelectedStudentsForBulkAction(() => {
      this.setState({
        isDeleteStudentModalOpen: true,
        deleteConfirm: ''
      });
    });
  };

  closeDeleteStudentModal = (): void => {
    this.setState({
      isDeleteStudentModalOpen: false,
      deleteConfirm: ''
    });
  };
  onNextOfKinSaveButtonClick = (data): void => {
    const { user } = this.props;
    const { personIndex, selectedItems } = this.state;
    const student = { ...selectedItems[0] };
    const nextOfKin = [...propz.get(student, ['nextOfKin'], [])];

    const { priority, phones, personEmail, ...rest } = data;
    const currentPhones = data.phones.filter(phone => phone !== '');

    const dataCopy = {
      ...rest,
      priority: typeof data.priority !== 'undefined' ? Number(data.priority) : undefined,
      phones: currentPhones,
      email: personEmail
    };

    if (personIndex !== -1) {
      student.nextOfKin[personIndex] = dataCopy;
    } else {
      nextOfKin.push(dataCopy);
      student.nextOfKin = nextOfKin;
    }

    this.setState({
      isLoading: true
    });

    updateSchoolStudent(user, student.id, student).then(student => {
      this.setState({
        isAddNextOfKinModalOpen: false,
        selectedItems: [student],
        personIndex: -1
      });
      this.setItems();
    });
  };

  onEditNextOfKinClick = (index): void => {
    this.setState({
      isAddNextOfKinModalOpen: true,
      personIndex: index
    });
  };

  onNextOfKinRemoveButtonClick = (index: number): void => {
    const { user } = this.props;
    const { selectedItems } = this.state;
    const student = { ...selectedItems[0] };
    const newNextOfKin = [...propz.get(student, ['nextOfKin'], [])];

    newNextOfKin.splice(index, 1);
    student.nextOfKin = newNextOfKin;

    this.setState({
      isLoading: true
    });

    updateSchoolStudent(user, student.id, student).then(student => {
      this.setState({
        selectedItems: [student]
      });
      this.setItems();
    });
  };

  getStudentSummaryTabs(): React.ReactNode {
    const { currentTabIndex, previewStudent } = this.state;

    if (!previewStudent) {
      return null;
    }

    switch (currentTabIndex) {
      case 0:
        return <StudentSummary student={previewStudent} />;
      case 1:
        return (
          <StudentNextOfKin
            student={previewStudent}
            onEditClick={this.onEditNextOfKinClick}
            onRemoveClick={this.onNextOfKinRemoveButtonClick}
          />
        );
      default:
        console.error('Students.tsx \n Wrong tab index ' + currentTabIndex);
        return null;
    }
  }

  isOneStudentSelected() {
    const selectedStudents = this.state.selectedItems;
    return selectedStudents.length === 1;
  }

  getStudentTags(): any[] {
    const { selectedItems } = this.state;

    return Lazy(selectedItems)
      .map(selectedItem => selectedItem.tags || [])
      .filter(tagList => tagList.length > 0)
      .flatten()
      .uniq('id')
      .toArray();
  }

  onTagChange = (event): void => {
    const value = event.target.value;

    this.setState({
      selectedTagId: value,
      selectedTagIds: value ? [value] : []
    });
  };

  onTagIdsChange = (selectedTagIds: string[]): void => {
    this.setState({
      selectedTagIds,
      selectedTagId: selectedTagIds[0] || ''
    });
  };
  onTagDeleteModalCloseClick = (): void => {
    this.setState({
      isTagDeleteModalOpen: false
    });
  };

  onTagDelete = (selectedTagIds?: string[]): void => {
    const studentTagIds = selectedTagIds && selectedTagIds.length ? selectedTagIds : this.state.selectedTagIds;
    const user = this.props.user;

    this.setState({
      isLoading: true
    });

    if (studentTagIds.length === 0) {
      this.setState({
        isTagDeleteModalOpen: false,
        selectedTagId: '',
        selectedTagIds: [],
        isLoading: false
      });
      console.error('Can not delete user tag id');
      return;
    }

    const selectedItems = this.state.selectedItems;

    updateStudentTagsInBulk(
      user,
      selectedItems.map(item => item.id),
      studentTagIds,
      'REMOVE'
    )
      .then(() => {
        this.setState({
          isTagDeleteModalOpen: false,
          selectedTagId: '',
          selectedTagIds: [],
          selectedItems: [],
          isSelectAllChecked: false,
          excludedSelectionIds: []
        });
        this.setItems();
      })
      .catch(error => {
        console.error('Can not remove student tags', error);
        this.setState({ isLoading: false });
      });
  };
  onUserTagSaveClick = (): void => {
    const tagIds = this.state.selectedTagIds;
    const user = this.props.user;

    this.setState({
      isLoading: true
    });

    if (tagIds.length === 0) {
      this.setState({
        isTagAssignModalOpen: false,
        selectedTagId: '',
        selectedTagIds: [],
        isLoading: false
      });
      console.error('Can not assign student tag id');
      return;
    }

    const selectedItems = this.state.selectedItems;
    updateStudentTagsInBulk(
      user,
      selectedItems.map(item => item.id),
      tagIds,
      'ADD'
    )
      .then(() => {
        this.setState({
          isTagAssignModalOpen: false,
          selectedTagId: '',
          selectedTagIds: [],
          selectedItems: [],
          isSelectAllChecked: false,
          excludedSelectionIds: []
        });
        this.setItems();
      })
      .catch(error => {
        console.error('Can not assign student tags', error);
        this.setState({ isLoading: false });
      });
  };
  onCreateUserTagFromAssignModal = (tagName: string, color: string): Promise<Tag> => {
    const { user } = this.props;

    return createUserTag(user, { tagName, color }).then((createdTag: Tag) => {
      this.setState(prevState => ({
        tags: [...prevState.tags, createdTag]
      }));
      return createdTag;
    });
  };
  renderTagModals(): React.ReactNode {
    const { tags, selectedTagId, selectedTagIds, isTagAssignModalOpen, isTagDeleteModalOpen } = this.state;
    const excludedTagIds = Array.from(
      new Set(
        this.state.selectedItems.reduce(
          (result: string[], student) => [...result, ...propz.get(student, ['tags'], []).map(tag => tag.id)],
          []
        )
      )
    );

    return (
      <>
        <UserTagDeleteModal
          modalTitle={'Delete Student Tag'}
          userTags={this.getStudentTags()}
          selectedUserTagId={selectedTagId}
          selectedUserTagIds={selectedTagIds}
          onUserTagChange={this.onTagChange}
          onCloseClick={this.onTagDeleteModalCloseClick}
          isOpen={isTagDeleteModalOpen}
          onDeleteClick={this.onTagDelete}
        />
        <UserTagAssignModal
          modalTitle={'Assign tags'}
          userTags={tags}
          excludedUserTagIds={excludedTagIds}
          selectedStudents={this.state.selectedItems}
          selectedUserTagId={selectedTagId}
          selectedUserTagIds={selectedTagIds}
          onUserTagChange={this.onTagChange}
          onUserTagIdsChange={this.onTagIdsChange}
          onCloseClick={this.onTagAssignModalCloseClick}
          isOpen={isTagAssignModalOpen}
          onCreateTag={this.onCreateUserTagFromAssignModal}
          onSaveClick={this.onUserTagSaveClick}
        />
      </>
    );
  }

  openBulkFormModal = (): void => {
    this.resolveSelectedStudentsForBulkAction(() =>
      this.setState({ isBulkFormModalOpen: true, selectedBulkFormId: '', bulkStudentGroupSearch: '' })
    );
  };

  openBulkHouseModal = (): void => {
    this.resolveSelectedStudentsForBulkAction(() =>
      this.setState({ isBulkHouseModalOpen: true, selectedBulkHouseId: '', bulkStudentGroupSearch: '' })
    );
  };

  closeBulkFormModal = (): void =>
    this.setState({ isBulkFormModalOpen: false, selectedBulkFormId: '', bulkStudentGroupSearch: '' });
  closeBulkHouseModal = (): void =>
    this.setState({ isBulkHouseModalOpen: false, selectedBulkHouseId: '', bulkStudentGroupSearch: '' });

  saveBulkStudentGroup = (kind: 'form' | 'house'): void => {
    const { selectedItems, selectedBulkFormId, selectedBulkHouseId } = this.state;
    const selectedId = kind === 'form' ? selectedBulkFormId : selectedBulkHouseId;
    if (!selectedId || selectedItems.length === 0) return;

    this.setState({ isLoading: true });
    BPromise.all(
      selectedItems.map(student => {
        const currentFormId =
          student.formId || propz.get(student, ['form', 'id'], propz.get(student, ['form', '_id'], undefined));
        const currentHouseId =
          student.houseId || propz.get(student, ['house', 'id'], propz.get(student, ['house', '_id'], undefined));
        const data = {
          ...student,
          formId: kind === 'form' ? selectedId : currentFormId,
          houseId: kind === 'house' ? selectedId : currentHouseId
        };

        return updateSchoolStudent(this.props.user, student.id, data);
      })
    )
      .then(() => {
        this.setState({
          isLoading: false,
          isBulkFormModalOpen: false,
          isBulkHouseModalOpen: false,
          selectedBulkFormId: '',
          selectedBulkHouseId: '',
          selectedItems: [],
          isSelectAllChecked: false,
          excludedSelectionIds: []
        });
        this.setItems();
      })
      .catch(error => {
        this.setState({
          isLoading: false,
          errorMessage: propz.get(error, ['response', 'data', 'message'], 'Unable to update the selected students.'),
          isErrorModalOpen: true
        });
      });
  };

  renderBulkStudentGroupModal(kind: 'form' | 'house'): React.ReactNode {
    const isForm = kind === 'form';
    const isOpen = isForm ? this.state.isBulkFormModalOpen : this.state.isBulkHouseModalOpen;
    if (!isOpen) return null;

    const items = isForm ? this.state.forms : this.state.houses;
    const search = this.state.bulkStudentGroupSearch.trim().toLocaleLowerCase();
    const collator = new Intl.Collator('en-GB', { numeric: true, sensitivity: 'base' });
    const visibleItems = items
      .filter(item => !search || item.name.toLocaleLowerCase().includes(search))
      .slice()
      .sort((left, right) => {
        if (isForm && left.age !== right.age) return left.age - right.age;
        return collator.compare(left.name, right.name);
      });
    const selectedId = isForm ? this.state.selectedBulkFormId : this.state.selectedBulkHouseId;
    const count = this.state.selectedItems.length;
    const close = isForm ? this.closeBulkFormModal : this.closeBulkHouseModal;
    const formStages = [
      { key: 'eyfs', label: 'EYFS', years: 'Reception', matches: (age: number) => age === 0 },
      { key: 'ks1', label: 'KS1', years: 'Y1–Y2', matches: (age: number) => age >= 1 && age <= 2 },
      { key: 'ks2', label: 'KS2', years: 'Y3–Y6', matches: (age: number) => age >= 3 && age <= 6 },
      { key: 'ks3', label: 'KS3', years: 'Y7–Y9', matches: (age: number) => age >= 7 && age <= 9 },
      { key: 'ks4', label: 'KS4', years: 'Y10–Y11', matches: (age: number) => age >= 10 && age <= 11 },
      { key: 'ks5', label: 'KS5', years: 'Y12–Y13', matches: (age: number) => age >= 12 && age <= 13 },
      {
        key: 'other',
        label: 'Other',
        years: '',
        matches: (age: number) => age < 0 || age > 13 || !Number.isFinite(age)
      }
    ];
    const groupedForms = formStages
      .map(stage => ({ ...stage, items: visibleItems.filter(item => stage.matches(Number(item.age))) }))
      .filter(stage => stage.items.length > 0);
    const renderChoice = (item: { id: string; name: string }) => (
      <button
        key={item.id}
        type="button"
        className={selectedId === item.id ? 'is-selected' : ''}
        onClick={() =>
          isForm ? this.setState({ selectedBulkFormId: item.id }) : this.setState({ selectedBulkHouseId: item.id })
        }
      >
        <span className="studentBulkGroup__check">✓</span>
        {item.name}
      </button>
    );

    return (
      <SimpleModal
        isOpen={isOpen}
        title={isForm ? 'Add students to reg form' : 'Add students to house'}
        customClass="studentBulkGroupModal"
        dialogStyle={{ maxWidth: 540 }}
        showFooter={false}
        isBodyBold={false}
        onCloseClick={close}
      >
        <div className="studentBulkGroup">
          <p>
            Choose one {isForm ? 'registration form' : 'house'} for {count} selected{' '}
            {count === 1 ? 'student' : 'students'}. Their current {isForm ? 'registration form' : 'house'} will be
            replaced.
          </p>
          <label className="studentBulkGroup__search">
            <span>Search {isForm ? 'registration forms' : 'houses'}</span>
            <input
              type="search"
              value={this.state.bulkStudentGroupSearch}
              placeholder={isForm ? 'Search by form name' : 'Search by house name'}
              onChange={event => this.setState({ bulkStudentGroupSearch: event.target.value })}
              autoFocus
            />
          </label>
          <div className="studentBulkGroup__choices">
            {isForm
              ? groupedForms.map(stage => (
                  <section className="studentBulkGroup__stage" key={stage.key}>
                    <div className="studentBulkGroup__stageHeader">
                      <strong>{stage.label}</strong>
                      {stage.years && <span>{stage.years}</span>}
                    </div>
                    <div className="studentBulkGroup__stageChoices">{stage.items.map(renderChoice)}</div>
                  </section>
                ))
              : visibleItems.map(renderChoice)}
          </div>
          {visibleItems.length === 0 ? (
            <div className="studentBulkGroup__empty">
              {items.length === 0
                ? `No ${isForm ? 'registration forms' : 'houses'} available.`
                : `No ${isForm ? 'registration forms' : 'houses'} match your search.`}
            </div>
          ) : null}
          <div className="studentBulkGroup__actions">
            <button type="button" className="people-btn people-btn--secondary" onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className="people-btn people-btn--primary"
              disabled={!selectedId}
              onClick={() => this.saveBulkStudentGroup(kind)}
            >
              Save changes
            </button>
          </div>
        </div>
      </SimpleModal>
    );
  }

  ensureHousePointsTypeAndOpenGrant = (targetStudents: SchoolStudent[]) => {
    const { user } = this.props;

    const schoolPicRaw = user?.activeSchool?.pic as string | undefined;
    const picture = schoolPicRaw
      ? schoolPicRaw.startsWith('//')
        ? `${window.location.protocol}${schoolPicRaw}`
        : schoolPicRaw
      : undefined;

    const filter = {
      where: { name: 'House Points', isSystemHidden: true },
      limit: 1
    };

    return getSchoolAwardTypes(user, filter)
      .then(types => {
        if (types.length) return types[0];

        const payload: any = {
          name: 'House Points',
          isSystemHidden: true,
          defaultHousePoints: 0,
          ...(picture ? { picture } : {})
        };

        return createAwardType(user, payload).catch(err => {
          const status = propz.get(err, ['response', 'status'], null);

          if (status === 409) {
            return getSchoolAwardTypes(user, filter).then(retryTypes => {
              if (retryTypes.length) return retryTypes[0];
              throw err;
            });
          }

          throw err;
        });
      })
      .then(awardType => {
        this.setState({
          preselectedAwardType: awardType,
          isGrantAwardOpen: true,
          grantAwardStudents: targetStudents
        });
      });
  };

  openGrantAwardModal = (): void => {
    this.resolveSelectedStudentsForBulkAction(() => {
      this.setState({ isGrantAwardOpen: true });
    });
  };

  awardHousePoints = async () => {
    const awardsActive = await isModuleActiveFresh(this.props.user, 'AWARDS');

    if (!awardsActive) {
      this.setState({
        isAwardsModuleActive: false,
        isGrantAwardOpen: true,
        preselectedAwardType: null,
        grantAwardStudents: null,
        isSelectionMenuOpen: false
      });
      return;
    }

    this.setState({ isAwardsModuleActive: true }, () => {
      this.resolveSelectedStudentsForBulkAction(() => this.awardHousePointsResolved());
    });
  };

  awardHousePointsResolved = () => {
    const { selectedItems } = this.state;
    if (!selectedItems || selectedItems.length === 0) return;

    const studentsWithoutHouse = selectedItems.filter(s => !this.getHouseId(s));
    const studentsWithHouse = selectedItems.filter(s => this.getHouseId(s));

    if (studentsWithHouse.length === 0) {
      this.setState({
        isNoHouseWarningOpen: true,
        studentsWithoutHouse
      });
      return;
    }

    if (studentsWithoutHouse.length > 0) {
      this.setState({
        isPartialHouseWarningOpen: true,
        studentsWithoutHouse
      });
      return;
    }

    this.ensureHousePointsTypeAndOpenGrant(studentsWithHouse).catch(err => {
      console.error('Could not ensure “House Points” award type', err);
      alert('Unable to award house points at the moment.');
    });
  };

  private closeNoHouseWarning = () => {
    this.setState({ isNoHouseWarningOpen: false, studentsWithoutHouse: [] });
  };

  private closePartialHouseWarning = () => {
    this.setState({ isPartialHouseWarningOpen: false });
  };

  private onConfirmGrantOnlyForWithHouse = () => {
    const { selectedItems, studentsWithoutHouse } = this.state;
    const noSet = new Set(studentsWithoutHouse.map(s => s.id));
    const withHouse = selectedItems.filter(s => !noSet.has(s.id));

    this.setState({ isPartialHouseWarningOpen: false }, () => {
      this.ensureHousePointsTypeAndOpenGrant(withHouse).catch(err => {
        console.error('Could not ensure “House Points” award type', err);
        alert('Unable to award house points at the moment.');
      });
    });
  };

  renderNoHouseWarningModal(): React.ReactNode {
    const { isNoHouseWarningOpen } = this.state;
    return (
      <SimpleModal
        isOpen={isNoHouseWarningOpen}
        title="Cannot grant house points"
        body={
          <div className="studentAwardNotice">
            <span className="studentAwardNotice__icon" aria-hidden="true">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </span>
            <div>
              <p>None of the selected students has a house assigned.</p>
              <small>Assign a house on the Students page, then try again.</small>
            </div>
          </div>
        }
        buttonText="OK"
        onButtonClick={this.closeNoHouseWarning}
        onCloseClick={this.closeNoHouseWarning}
        hideCancelButton
        isBodyBold={false}
        customClass="studentAwardNoticeModal"
        dialogStyle={{ maxWidth: 460 }}
      />
    );
  }

  renderPartialHouseWarningModal(): React.ReactNode {
    const { isPartialHouseWarningOpen, studentsWithoutHouse } = this.state;
    const total = studentsWithoutHouse.length;

    // Show up to N names, then a "+ X more" item
    const MAX = 8;
    const names = studentsWithoutHouse.map(s => getFullName(s));
    const visible = names.slice(0, MAX);
    const extra = total - visible.length;

    return (
      <SimpleModal
        isOpen={isPartialHouseWarningOpen}
        title="Some students aren’t in a house"
        buttonText="Continue for students with a house"
        buttonCancelText="Go back"
        onButtonClick={this.onConfirmGrantOnlyForWithHouse}
        onCloseClick={this.closePartialHouseWarning}
        customClass="studentAwardNoticeModal"
        dialogStyle={{ maxWidth: 520 }}
      >
        <div className="studentAwardNotice studentAwardNotice--stacked">
          <div className="studentAwardNotice__lead">
            <span className="studentAwardNotice__icon" aria-hidden="true">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </span>
            <p>
              <strong>
                {total} student{total === 1 ? '' : 's'} cannot receive house points
              </strong>{' '}
              because no house is assigned to them.
            </p>
          </div>

          <ul className="studentAwardNotice__studentList">
            {visible.map((n, i) => (
              <li key={i}>
                <span>{i + 1}</span>
                <b>{n}</b>
              </li>
            ))}
            {extra > 0 && <li className="studentAwardNotice__more">+{extra} more</li>}
          </ul>

          <div className="studentAwardNotice__hint">
            Continue to grant points only to students who have a house, or go back and update the assignments.
          </div>
        </div>
      </SimpleModal>
    );
  }

  renderGrantAwardModal() {
    const {
      isGrantAwardOpen,
      preselectedAwardType,
      selectedItems,
      grantAwardStudents,
      isAwardsModuleActive
    } = this.state;
    const { user } = this.props;

    if (!isGrantAwardOpen) return null;

    if (isAwardsModuleActive === false) {
      return (
        <ModuleUpsellModal
          isOpen={isGrantAwardOpen}
          onClose={() =>
            this.setState({
              isGrantAwardOpen: false,
              preselectedAwardType: null,
              grantAwardStudents: null,
              isAwardsModuleActive: undefined
            })
          }
          module="AWARDS"
        />
      );
    }

    const targetStudents = grantAwardStudents && grantAwardStudents.length ? grantAwardStudents : selectedItems;

    if (!targetStudents || targetStudents.length === 0) return null;

    if (isAwardsModuleActive === undefined) {
      // Still checking — trigger async check and show nothing yet
      isModuleActiveFresh(user, 'AWARDS').then(active => {
        this.setState({ isAwardsModuleActive: active });
      });
      return null;
    }

    return (
      <GrantAwardModal
        isOpen={isGrantAwardOpen}
        onClose={() =>
          this.setState({
            isGrantAwardOpen: false,
            preselectedAwardType: null,
            grantAwardStudents: null,
            isAwardsModuleActive: undefined
          })
        }
        students={targetStudents}
        user={user}
        preselectedType={preselectedAwardType}
      />
    );
  }

  onStudentSaveButtonClick = (data: any, isContinue: boolean, isSavingSimilarStudentConfirmed) => {
    const { user } = this.props;
    const { selectedItems, isCreateStudentsChain, isEditStudent } = this.state;
    const selectedItemsCount = selectedItems.length;
    const { firstName: studentToAddFirstName, lastName: studentToAddLastName, birthday: studentToAddBirthday } = data;

    this.setState({
      isLoading: true,
      identicalStudents: [],
      similarStudents: []
    });

    if (data.formId === '') {
      delete data.formId;
    }

    if (data.houseId === '') {
      delete data.houseId;
    }

    const sameNameCriteria = {
      firstName: {
        like: studentToAddFirstName.trim(),
        options: 'i'
      },
      lastName: {
        like: studentToAddLastName.trim(),
        options: 'i'
      }
    };
    const hasBirthday = Boolean(studentToAddBirthday);
    const birthdayCriteria = hasBirthday
      ? {
          $gte: new Date(studentToAddBirthday),
          $lte: new Date(studentToAddBirthday)
        }
      : undefined;
    const similarWhere = hasBirthday
      ? {
          $or: [
            sameNameCriteria,
            {
              lastName: { like: studentToAddLastName.trim(), options: 'i' },
              birthday: birthdayCriteria
            },
            {
              firstName: { like: studentToAddFirstName.trim(), options: 'i' },
              birthday: birthdayCriteria
            }
          ]
        }
      : sameNameCriteria;
    const similarStudentFilter = {
      limit: DEFAULT_LIMIT,
      skip: DEFAULT_SKIP,
      where: similarWhere
    };

    return searchSchoolStudents(user, similarStudentFilter)
      .then(({ items: allFoundSimilarStudentsUniq }) => {
        const currentStudentId = isEditStudent && selectedItemsCount === 1 ? selectedItems[0].id : '';
        const studentsForDuplicateCheck = allFoundSimilarStudentsUniq.filter(
          similarStudent => similarStudent.id !== currentStudentId
        );

        const identicalStudents = studentsForDuplicateCheck.filter(similarStudent => {
          const {
            firstName: similarStudentFirstName,
            lastName: similarStudentLastName,
            birthday: similarStudentBirthday
          } = similarStudent;

          const similarStudentFirstNameFormatted = similarStudentFirstName.toLowerCase().trim();
          const similarStudentLastNameFormatted = similarStudentLastName.toLowerCase().trim();

          const studentToAddFirstNameFormatted = studentToAddFirstName.toLowerCase().trim();
          const studentToAddLastNameFormatted = studentToAddLastName.toLowerCase().trim();

          return (
            similarStudentFirstNameFormatted === studentToAddFirstNameFormatted &&
            similarStudentLastNameFormatted === studentToAddLastNameFormatted &&
            similarStudentBirthday === studentToAddBirthday
          );
        });

        const similarStudents = studentsForDuplicateCheck.filter(similarStudent => {
          const {
            firstName: similarStudentFirstName,
            lastName: similarStudentLastName,
            birthday: similarStudentBirthday
          } = similarStudent;

          const similarStudentFirstNameFormatted = similarStudentFirstName.toLowerCase().trim();
          const similarStudentLastNameFormatted = similarStudentLastName.toLowerCase().trim();

          const studentToAddFirstNameFormatted = studentToAddFirstName.toLowerCase().trim();
          const studentToAddLastNameFormatted = studentToAddLastName.toLowerCase().trim();

          const isStudentToAddWithoutBirthday = !hasBirthday;

          if (isStudentToAddWithoutBirthday) {
            return (
              similarStudentFirstNameFormatted === studentToAddFirstNameFormatted &&
              similarStudentLastNameFormatted === studentToAddLastNameFormatted
            );
          }

          return (
            (similarStudentFirstNameFormatted === studentToAddFirstNameFormatted &&
              similarStudentLastNameFormatted === studentToAddLastNameFormatted &&
              similarStudentBirthday !== studentToAddBirthday) ||
            (similarStudentFirstNameFormatted === studentToAddFirstNameFormatted &&
              similarStudentBirthday === studentToAddBirthday &&
              similarStudentLastNameFormatted !== studentToAddLastNameFormatted) ||
            (similarStudentLastNameFormatted === studentToAddLastNameFormatted &&
              similarStudentBirthday === studentToAddBirthday &&
              similarStudentFirstNameFormatted !== studentToAddFirstNameFormatted)
          );
        });

        const isIdenticalStudentsExist = identicalStudents.length > 0;

        if (
          (isSavingSimilarStudentConfirmed && !isIdenticalStudentsExist) ||
          studentsForDuplicateCheck.length === 0 ||
          isEditStudent
        ) {
          switch (true) {
            case selectedItemsCount === 0 || isCreateStudentsChain:
              return addSchoolStudent(user, data).then(student => {
                this.setState(prevState => ({
                  isEditStudentModalOpen: isContinue,
                  selectedItems: isContinue ? [student, ...prevState.selectedItems] : [student],
                  isCreateStudentsChain: isContinue,
                  similarStudents: [],
                  identicalStudents: [],
                  studentToAdd: undefined
                }));

                return this.setItems();
              });

            case selectedItemsCount === 1:
              const studentId = selectedItems[0].id;
              const nextOfKin = propz.get(selectedItems[0], ['nextOfKin'], []);
              const dataUpdated = {
                ...data,
                nextOfKin: typeof data.nextOfKin !== 'undefined' ? data.nextOfKin : nextOfKin
              };
              return updateSchoolStudent(user, studentId, dataUpdated).then(studentUpdated => {
                this.setState({
                  isEditStudentModalOpen: false,
                  isEditStudent: false,
                  selectedItems: [studentUpdated],
                  isCreateStudentsChain: false,
                  similarStudents: [],
                  identicalStudents: [],
                  studentToAdd: undefined
                });

                return this.setItems();
              });

            default:
              return BPromise.resolve(true);
          }
        } else {
          this.setState({
            identicalStudents,
            similarStudents,
            studentToAdd: data,
            isLoading: false
          });
        }
      })
      .catch(error => {
        console.error('Can not validate or save student', error);
        this.setState({ isLoading: false });
        return false;
      });
  };

  onStudentCancelEditButtonClick = (): void => {
    this.setState({
      isEditStudentModalOpen: false,
      isEditStudent: false,
      isCreateStudentsChain: false,
      similarStudents: [],
      identicalStudents: [],
      studentToAdd: undefined
    });
  };

  renderStudentFormModal(): React.ReactNode {
    const {
      isEditStudentModalOpen,
      selectedItems,
      isCreateStudentsChain,
      identicalStudents,
      similarStudents,
      studentToAdd,
      forms,
      isEditStudent
    } = this.state;
    const { user } = this.props;
    const selectedItemsCount = selectedItems.length;
    const isSelectedItemExist = selectedItemsCount > 0;
    const isFormsExist = forms.length > 0;
    if (!isEditStudentModalOpen) {
      return null;
    }

    return (
      <StudentForm
        student={isEditStudent && isSelectedItemExist ? selectedItems[0] : studentToAdd}
        onSubmit={this.onStudentSaveButtonClick}
        onCancel={this.onStudentCancelEditButtonClick}
        user={user}
        isCreateStudentsChain={isCreateStudentsChain}
        identicalStudents={identicalStudents}
        similarStudents={similarStudents}
        isFormsExist={isFormsExist}
        isEditStudent={isEditStudent}
        fullPage={true}
      />
    );
  }

  onMessageSubmitClick = message => {
    const { history } = this.props;

    history.push({
      pathname: '/generalMessages',
      state: { message: message }
    });
  };

  onCloseMessageWizardClick = (): void => {
    this.setState({
      isMessageModalOpen: false
    });
  };
  renderMessageAudienceModal(): React.ReactNode {
    const { isMessageAudienceModalOpen, messageRecipients, selectedItems } = this.state;

    if (!isMessageAudienceModalOpen) {
      return null;
    }

    const selectedCount = selectedItems.length;
    const hasLinkedParents = selectedItems.some(student => propz.get(student, ['parents'], []).length > 0);
    const options: Array<{ key: 'students' | 'parents' | 'both'; icon: any; title: string; text: string }> = [
      {
        key: 'students',
        icon: faGraduationCap,
        title: 'Students only',
        text: 'Send the message to the selected student accounts.'
      },
      ...(hasLinkedParents
        ? ([
            {
              key: 'parents',
              icon: faUsers,
              title: 'Parents only',
              text: 'Send the message to parents and guardians linked to the selected students.'
            },
            {
              key: 'both',
              icon: faEnvelope,
              title: 'Students and parents',
              text: 'Send one message to both selected students and their parents.'
            }
          ] as Array<{ key: 'parents' | 'both'; icon: any; title: string; text: string }>)
        : [])
    ];

    return (
      <SimpleModal
        isOpen={isMessageAudienceModalOpen}
        onCloseClick={this.onCloseMessageAudienceModalClick}
        onCloseIconClick={this.onCloseMessageAudienceModalClick}
        customClass="people-messageDialog"
        dialogStyle={{ maxWidth: 640 }}
        zIndex={2147483100}
        showHeader={false}
        showFooter={false}
        isBodyBold={false}
      >
        <div className="people-messageModal">
          <div className="people-messageModal__head">
            <span className="people-messageModal__icon">
              <FontAwesomeIcon icon={faEnvelope} />
            </span>
            <span className="people-messageModal__eyebrow">{this.getGeneralMessagesVersionLabel()}</span>
            <h2>Create message</h2>
            <p>Choose who should receive this message. You can review and edit the content before anything is sent.</p>
          </div>
          <div className="people-messageModal__recap">
            <span className="people-messageModal__avatars" aria-hidden="true">
              {selectedItems.slice(0, 4).map(student => (
                <i key={student.id}>{`${propz.get(student, ['firstName'], '?').charAt(0)}${propz
                  .get(student, ['lastName'], '')
                  .charAt(0)}`}</i>
              ))}
            </span>
            <span>
              Messaging <b>{selectedCount}</b> student{selectedCount === 1 ? '' : 's'}.
            </span>
          </div>
          <div className="people-messageChoices">
            {options.map(option => (
              <button
                key={option.key}
                type="button"
                className={`people-messageChoice ${
                  messageRecipients === option.key ? 'people-messageChoice--active' : ''
                }`}
                onClick={() => this.setState({ messageRecipients: option.key })}
              >
                <span className="people-messageChoice__icon">
                  <FontAwesomeIcon icon={option.icon} />
                </span>
                <span className="people-messageChoice__body">
                  <strong>{option.title}</strong>
                  <em>{option.text}</em>
                </span>
                <span className="people-messageChoice__check">
                  <FontAwesomeIcon icon={faCheck} />
                </span>
              </button>
            ))}
          </div>
          <div className="people-messageModal__foot">
            <button
              type="button"
              className="people-btn people-btn--secondary"
              onClick={this.onCloseMessageAudienceModalClick}
            >
              Cancel
            </button>
            <button
              type="button"
              className="people-btn people-btn--primary"
              onClick={this.onMessageAudienceContinueClick}
            >
              <FontAwesomeIcon icon={faEnvelope} /> Continue
            </button>
          </div>
        </div>
      </SimpleModal>
    );
  }

  renderGeneralMessagesUpsellModal(): React.ReactNode {
    const { isGeneralMessagesUpsellOpen } = this.state;

    if (!isGeneralMessagesUpsellOpen) {
      return null;
    }

    return (
      <SimpleModal
        isOpen={isGeneralMessagesUpsellOpen}
        onCloseClick={this.closeGeneralMessagesUpsell}
        onCloseIconClick={this.closeGeneralMessagesUpsell}
        customClass="people-gmUpsellDialog"
        dialogStyle={{ maxWidth: 460 }}
        zIndex={2147483100}
        showHeader={false}
        showFooter={false}
        isBodyBold={false}
      >
        <div className="people-gmUpsell">
          <span className="people-gmUpsell__badge">Add-on module</span>
          <div className="people-gmUpsell__hero">
            <span className="people-gmUpsell__icon">
              <FontAwesomeIcon icon={faEnvelope} />
            </span>
            <span className="people-gmUpsell__lock">
              <FontAwesomeIcon icon={faLock} />
            </span>
          </div>
          <h2>General Messages is not switched on</h2>
          <p>
            Use General Messages to reach selected students, parents and staff with targeted announcements from one
            place.
          </p>
          <ul>
            <li>
              <FontAwesomeIcon icon={faCheck} /> Send to parents, students, or both at the same time
            </li>
            <li>
              <FontAwesomeIcon icon={faCheck} /> Personalise messages and reuse existing templates
            </li>
            <li>
              <FontAwesomeIcon icon={faCheck} /> Track delivery and read status after sending
            </li>
          </ul>
          <div className="people-gmUpsell__note">
            No rush. It is not available for this school right now, but it can be enabled from Modules & plans when you
            are ready.
          </div>
          <div className="people-gmUpsell__foot">
            <button
              type="button"
              className="people-btn people-btn--secondary"
              onClick={this.closeGeneralMessagesUpsell}
            >
              Not now
            </button>
            <button
              type="button"
              className="people-btn people-btn--primary"
              onClick={() => {
                this.closeGeneralMessagesUpsell();
                this.props.history.push('/school/modules');
              }}
            >
              Go to Modules &amp; plans
            </button>
          </div>
        </div>
      </SimpleModal>
    );
  }
  renderGMWizard(): React.ReactNode {
    const { user } = this.props;
    const { isMessageModalOpen, selectedItems, maxSizeUploadFile, messageRecipients } = this.state;

    if (!isMessageModalOpen) {
      return null;
    }

    return (
      <GeneralMessageWizzard
        user={user}
        onMessageSubmit={this.onMessageSubmitClick}
        onCancelClick={this.onCloseMessageWizardClick}
        sendToStudents={messageRecipients === 'students' || messageRecipients === 'both'}
        sendToParents={messageRecipients === 'parents' || messageRecipients === 'both'}
        sendToStaff={false}
        isOpen={isMessageModalOpen}
        students={selectedItems}
        maxSizeUploadFile={maxSizeUploadFile}
        fullScreen={true}
      />
    );
  }

  render() {
    const {
      isLoading,
      isMessageModalOpen,
      isMessageAudienceModalOpen,
      isGeneralMessagesUpsellOpen,
      isEditStudentModalOpen,
      isAddNextOfKinModalOpen,
      isStudentParticipationInEventsReportModalOpen,
      isStudentParticipationStatisticsByActivitiesReportModalOpen,
      isClubListForStudentReportModalOpen,
      isSelectedStudentTargetCompletionReportModalOpen,
      isReportsSelectionModalOpen,
      isErrorModalOpen
    } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    if (isEditStudentModalOpen) {
      return <div className="student-editorPage">{this.renderStudentFormModal()}</div>;
    }

    const classes =
      isMessageModalOpen ||
      isEditStudentModalOpen ||
      isErrorModalOpen ||
      isStudentParticipationInEventsReportModalOpen ||
      isStudentParticipationStatisticsByActivitiesReportModalOpen ||
      isClubListForStudentReportModalOpen ||
      isSelectedStudentTargetCompletionReportModalOpen ||
      isReportsSelectionModalOpen ||
      isAddNextOfKinModalOpen ||
      isMessageAudienceModalOpen ||
      isGeneralMessagesUpsellOpen ||
      this.state.isNoHouseWarningOpen ||
      this.state.isPartialHouseWarningOpen
        ? 'schoolPeoplePage modal-open'
        : 'schoolPeoplePage';

    return (
      <div className={classes}>
        {/*Place for render modal windows*/}
        {this.renderMessageAudienceModal()}
        {this.renderGeneralMessagesUpsellModal()}
        {this.renderTagModals()}
        {this.renderBulkStudentGroupModal('form')}
        {this.renderBulkStudentGroupModal('house')}
        {this.renderGrantAwardModal()}
        {this.renderStudentFormModal()}
        {this.renderReportsSelectionModal()}
        {this.renderStudentParticipationInEventsReportFormModal()}
        {this.renderStudentParticipationStatisticsByActivitiesReportFormModal()}
        {this.renderClubListForStudentReportFormModal()}
        {this.renderSelectedStudentTargetCompletionReportFormModal()}
        {this.renderDeleteStudentModal()}
        {this.renderNoHouseWarningModal()}
        {this.renderPartialHouseWarningModal()}
        <PeopleListPageHeader title="Students" total={this.state.total} />
        <div className="people-pageWrap">
          {this.renderStudentsTable()}
          {this.renderSummary()}
          {this.renderAddNextOfKinModal()}
        </div>
      </div>
    );
  }
}
